import type { User } from "firebase/auth";
import { readImageFileAsDataUrl } from "@/character/frame";
import type { GameArtOptions } from "@/game/main";
import {
  characterSheetModalMarkup,
  mountCharacterSheetModal,
} from "@/character/character-sheet-modal";
import { mountSkillsPanel } from "@/character/skills-page";
import { skillsModalMarkup } from "@/character/skills-modal";
import { migrateLegacyGlobalCooldownCards } from "@/character/skills/cooldown-upgrade";
import { saveSkillBar } from "@/character/skills/storage";
import { equipmentStatBonus, loadEquipmentState } from "@/character/equipment/storage";
import {
  characterCombatPower,
  mergeStatBonuses,
  playerCombatMaxHp,
  playerRatings,
} from "@/game/model/character-stats";
import {
  combatManaRegenPerSec,
  playerCombatMaxMana,
  SKILL_MANA_COST,
} from "@/game/model/combat-mana";
import { loadMyRankCosmetics } from "@/rank/load-rank-state";
import { weeklyRankBuffHint } from "@/rank/format-weekly-buff-hint";
import { syncPlayerPublicStats } from "@/cloud/sync-player-stats";
import { equippedPet, loadPetState } from "@/pet/firestore-storage";
import { buildPetSkillRuntimePayload } from "@/pet/skills/runtime";
import type { PetPlayerState } from "@/pet/types";
import { equippedShadow, loadShadowState } from "@/shadow/firestore-storage";
import { mergeProfileAndShadowGameArt } from "@/shadow/merge-game-art";
import { shadowEquippedStatBonus } from "@/shadow/shadow-stat-bonus";
import type { ShadowPlayerState } from "@/shadow/types";
import { resolveGmAccess } from "@/auth/gm-access";
import { isFirebaseConfigured } from "@/auth/firebase";
import { signOutUser } from "@/auth/google-auth";
import {
  DUNGEON_MAX_FLOORS_CAP,
  grantDungeonFloorSlots,
  loadDungeonMeta,
  saveDungeonMeta,
} from "@/dungeon/dungeon-meta";
import type { CharacterProfile } from "@/character/storage";
import {
  grantExp,
  loadCharacterProgression,
  saveCharacterProgression,
  type CharacterProgression,
} from "@/game/model/character-progression";
import {
  applyHpRegenTick,
  combatRatingsHpRegenPerSec,
} from "@/game/model/player-hp-regen";
import {
  CombatKillStreakEvents,
  loadCombatBestKillStreak,
} from "@/game/model/kill-streak";
import { openDungeonModal } from "@/mount-dungeon-modal";
import { openRaidModal } from "@/mount-raid-modal";
import { openRankModal } from "@/mount-rank-modal";
import { openTowerModal } from "@/mount-tower-modal";
import { applySkillChainGcdToOtherSlotsRecord } from "@/character/skills/skill-chain-gcd";
import {
  bumpSlotCooldownRecord,
  updateSkillBarCooldownDom,
} from "@/character/skills/skill-bar-cooldown";
import {
  TowerEvents,
  type TowerFocusTargetChangedPayload,
  type TowerPlayerHpChangedPayload,
  type TowerPlayerManaChangedPayload,
  type TowerSkillUseAcceptedPayload,
  type TowerSkillUseRejectedPayload,
} from "@/tower/tower-events";
import {
  HOME_PART_DRAG_MIME,
  HomeEvents,
  type HomeMovePlacementPayload,
  type HomeProgressionChangedPayload,
  type HomeAdjustPartDepthBiasPayload,
  type HomeAdjustPlacementTransformPayload,
  type HomeSelectPlacementPayload,
  type HomeSitRestPayload,
} from "@/home/home-events";
import { mountHomeBootLoader } from "@/home/home-boot-loader";
import {
  hydrateGmMonsterCatalog,
  loadGmMonsterCatalog,
  mergeGmMonsterCatalogIntoHome,
} from "@/gm/gm-monster-catalog";
import { buyDungeonMonsterForVault } from "@/dungeon/dungeon-floor-purchases";
import {
  hydrateDungeonShopConfig,
  loadDungeonShopConfigCached,
} from "@/dungeon/load-dungeon-shop-config";
import type { DungeonShopConfig } from "@/dungeon/dungeon-shop-types";
import { hydrateGmPartCatalog, mergeGmPartCatalogIntoHome } from "@/gm/gm-part-catalog";
import { showHomeLevelUpDomFx } from "@/home/level-up-fx";
import { mountKindPicker, type KindPickerHandle } from "@/home/kind-picker";
import { SKILL_SLOT_MAX, skillCooldownMs } from "@/character/skills/constants";
import { skillEffectiveCooldownMs } from "@/character/skills/cooldown-upgrade";
import { buildSkillRuntimePayload } from "@/character/skills/runtime";
import { loadSkillBar, skillById } from "@/character/skills/storage";
import { createDefaultSkillBar, type SkillBarState } from "@/character/skills/types";
import {
  getGmShopItemById,
  hydrateGmShopCatalog,
  listGmShopItemsForPlayers,
  maxAffordableShopQuantity,
  purchaseGmShopItemQuantity,
} from "@/gm/gm-shop-catalog";
import {
  CATALOG_SLOT_PACK_GEM_COST,
  CATALOG_SLOT_PACK_SIZE,
  canAddCatalogPart,
  catalogSlotCap,
  catalogSlotRemaining,
  catalogSlotUsed,
  applyWalletGrant,
  createDefaultHomeEconomy,
  normalizeHomeEconomy,
  slotLabelTh,
  takeDraftsWithinSlots,
  totalCatalogCap,
  totalCatalogUsed,
  discardStashItems,
  setCombatHpCurrent,
  setCombatManaCurrent,
  setItemHotbarSlot,
  setItemHotbarSlotAuto,
  shouldPersistCombatHp,
  shouldPersistCombatMana,
  useHealthPotion,
  useManaPotion,
  type HomeEconomy,
  type StashDiscardItemId,
  type WalletGrant,
} from "@/home/economy";
import {
  currencyShopModalMarkup,
  defaultShopPageByTab,
  gemKindFromSelect,
  parseCurrencyShopTab,
  refreshCurrencyShopPanelDom,
  shopPageForItem,
  patchShopDetailQuantity,
  syncShopDetailDom,
  type CurrencyShopTab,
  type ShopPageByTab,
} from "@/home/home-currency-shop";
import {
  cycleStashSortMode,
  defaultStashViewState,
  getStashSlotById,
  refreshStashModalDom,
  stashModalMarkup,
  type StashItemId,
  type StashViewState,
} from "@/home/home-stash-modal";
import { tierFromStashItemId } from "@/home/health-potions";
import { tierFromManaStashItemId } from "@/home/mana-potions";
import {
  bindItemHotbar,
  shouldSuppressStashOpenClick,
  hotbarUseBlockedReason,
  renderItemHotbarHtml,
  syncItemHotbar,
  syncItemHotbarCooldownState,
} from "@/home/home-item-hotbar";
import {
  bindPlaySkillBar,
  emitPlaySkillBarMasterAutoCombat,
  playSkillBarRowMarkup,
  renderPlaySkillBarSlotsHtml,
  syncPlaySkillBarMasterAutoUi,
  syncPlaySkillBarSitRestUi,
} from "@/home/home-play-skill-bar";
import {
  COMBAT_SIT_REST_REGEN_MULT,
  emitCombatSitRestChanged,
} from "@/home/home-combat-sit-rest";
import {
  itemHotbarUseCooldownRemainingMs,
  itemHotbarUseReady,
  itemHotbarAutoUseReady,
  markItemHotbarAutoUsed,
  markItemHotbarUsed,
  pickAutoHotbarManaPotionSlot,
  pickAutoHotbarPotionSlot,
  tryAutoHotbarPotionOnce,
} from "@/home/item-hotbar-use";
import { HOME_ASSETS } from "@/home/home-assets";
import { loadHomeEconomyFromIdb, saveHomeEconomyToIdb } from "@/home/home-idb";
import { homeIcon, homeRailTileIcon } from "@/home/home-icons";
import { homeRailIcon } from "@/home/home-rail-icons";
import {
  applyHomeRailGridLayout,
  getHomeRailMenuLayout,
  hydrateHomeRailMenuLayout,
  renderHomeRailFrameMarkup,
  type HomeRailMenuFeatures,
} from "@/home/home-rail-menu-config";
import {
  mountHomeSheetCrop,
  type HomePartDraft,
  type HomeSheetCropHandle,
  type HomeSheetUploadPreset,
} from "@/home/mount-sheet-crop";
import {
  canPlaceAt,
  effectivePlacementDepthBias,
  effectivePlacementPlaceScale,
  findFirstPlaceCell,
  partFootprint,
  resolvePlaceCell,
} from "@/home/placement";
import { defaultZonesForKind, type HomeZone } from "@/home/zones";
import {
  clampPlacementDepthBiasForGrid,
  HOME_SHOP_TABS,
  kindBadgeLabels,
  kindPreset,
  normalizePartKind,
  type HomePartKind,
  type HomeShopTab,
} from "@/home/part-kind";
import {
  HOME_CELL_PX,
  HOME_GRID_COLS_MAX,
  HOME_GRID_COLS_MIN,
  HOME_GRID_ROWS_MAX,
  HOME_GRID_ROWS_MIN,
} from "@/home/grid";
import {
  applyGridSizeToRoom,
  applyThemeToRoom,
  clampPartCells,
  clampPlaceScale,
  createPartDefinition,
  HOME_PLACEMENT_DEPTH_BIAS_MAX,
  HOME_PLACEMENT_DEPTH_BIAS_MIN,
  HOME_PLACE_SCALE_MAX,
  HOME_PLACE_SCALE_MIN,
  HOME_PLACE_SCALE_STEP,
  kindQuotaCap,
  kindQuotaRemaining,
  kindQuotaUsed,
  normalizePlacementRotation,
  placeScaleFromPercent,
  placeScalePercent,
  newHomeId,
  resolveStageShellUrl,
  type HomeRoomData,
} from "@/home/storage";
import {
  fetchServerDate,
  formatUtcClockCompact,
  formatUtcClockLabel,
  themeIdFromClock,
} from "@/home/server-clock";
import { themeById } from "@/home/themes";
import { imageDataUrlForDisplay, imageDataUrlForStorage } from "@/home/image-data-url";
import {
  applyAutoBackgroundRemovalFromDataUrl,
  bindAutoRemoveBgCheckbox,
  homeAutoRemoveBgToggleMarkup,
} from "@/home/remove-white-bg";
import { GameEvents, type GameModeChangedPayload } from "@/game/events/game-events";
import {
  loadGameAccessMode,
  userCanAccessGmMode,
  type GameAccessMode,
} from "@/game/game-mode";
import { permissionsForGameMode } from "@/game/game-mode-permissions";
import { mountGameModeControl, type GameModeControlHandle } from "@/game/mount-game-mode-control";
import { SceneKeys } from "@/game/data/scene-keys";
import { HomeRoomScene } from "@/game/scenes/HomeRoomScene";
import { startHomeRoom } from "@/game/main";
import { bindHomeEditLayerPanel } from "@/home/home-edit-layer-panel";
import { mountHomeStructureFeatures } from "@/home/home-structure-mount";
import { mountGmHomeTools } from "@/home/mount-gm-home-tools";
import type Phaser from "phaser";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

type HomeModalId = "single";

/** HTML5 drag blocks touch scroll on palette chips; desktop mouse only. */
const paletteChipHtmlDrag = window.matchMedia(
  "(hover: hover) and (pointer: fine)",
).matches;

const MODAL_TITLES: Record<HomeModalId, string> = {
  single: "อัปโหลดรูป",
};

export type HomeSceneEditorFeatures = {
  walletHud: boolean;
  healthPotion: boolean;
  cashShop: boolean;
  skillsBar: boolean;
  skillsModals: boolean;
  characterSheet: boolean;
  towerNav: boolean;
  gameModeControl: boolean;
  gmTools: boolean;
  progressionHud: boolean;
  mergeGmCatalogs: boolean;
  serverScheduledTheme: boolean;
};

export const HOME_SCENE_EDITOR_FEATURES: HomeSceneEditorFeatures = {
  walletHud: true,
  healthPotion: true,
  cashShop: true,
  skillsBar: true,
  skillsModals: true,
  characterSheet: true,
  towerNav: true,
  gameModeControl: true,
  gmTools: true,
  progressionHud: true,
  mergeGmCatalogs: true,
  serverScheduledTheme: true,
};

export const CREATOR_SCENE_EDITOR_FEATURES: HomeSceneEditorFeatures = {
  walletHud: true,
  healthPotion: false,
  cashShop: false,
  skillsBar: false,
  skillsModals: false,
  characterSheet: false,
  towerNav: false,
  gameModeControl: false,
  gmTools: false,
  progressionHud: false,
  mergeGmCatalogs: true,
  serverScheduledTheme: true,
};

export type HomeSceneEditorShell = "home" | "creator" | "tower";

export type MountHomeSceneEditorOptions = {
  user: User;
  profile: CharacterProfile;
  profileName: string;
  avatarSrc: string;
  gameArt: GameArtOptions;
  sceneId: string;
  hudSubtitle: string;
  pageRootClass: string;
  shell: HomeSceneEditorShell;
  features: HomeSceneEditorFeatures;
  loadRoom: () => Promise<HomeRoomData>;
  saveRoom: (data: HomeRoomData) => Promise<void>;
  skillsQueryStripPath?: string;
  /** Mount editor UI into this element (outer `root` unchanged). */
  surfaceElement?: HTMLElement;
  /** Tower/dungeon arena layout while keeping home shell chrome. */
  arenaLayout?: boolean;
  fixedGrid?: { cols: number; rows: number };
  /** Open edit mode + shop when ready (tower / embedded). */
  autoEnterEditMode?: boolean;
  structureRaid?: {
    defenderUid: string;
    runId: string;
    onAssaultEnded: (detail: { damageThisRun: number; destroyed: boolean }) => void;
  };
};

export type HomeSceneEditorHandle = {
  destroy: () => void;
  getRoomData: () => HomeRoomData;
  flushSave: () => void;
};

/** Layout-only fields restored when discarding an edit session (catalog parts persist). */
type HomeLayoutEditSnapshot = Pick<
  HomeRoomData,
  | "placements"
  | "gridCols"
  | "gridRows"
  | "themeId"
  | "backdropPartId"
  | "backdropUrl"
  | "shellPartId"
  | "floorPartId"
>;

function captureHomeLayoutEditSnapshot(data: HomeRoomData): HomeLayoutEditSnapshot {
  return {
    placements: structuredClone(data.placements),
    gridCols: data.gridCols,
    gridRows: data.gridRows,
    themeId: data.themeId,
    backdropPartId: data.backdropPartId,
    backdropUrl: data.backdropUrl,
    shellPartId: data.shellPartId,
    floorPartId: data.floorPartId,
  };
}

function applyHomeLayoutEditSnapshot(
  target: HomeRoomData,
  snapshot: HomeLayoutEditSnapshot,
): void {
  target.placements = structuredClone(snapshot.placements);
  target.gridCols = snapshot.gridCols;
  target.gridRows = snapshot.gridRows;
  target.themeId = snapshot.themeId;
  target.backdropPartId = snapshot.backdropPartId;
  target.backdropUrl = snapshot.backdropUrl;
  target.shellPartId = snapshot.shellPartId;
  target.floorPartId = snapshot.floorPartId;
}

export const TOWER_SCENE_EDITOR_FEATURES: HomeSceneEditorFeatures = {
  walletHud: false,
  healthPotion: false,
  cashShop: false,
  skillsBar: false,
  skillsModals: false,
  characterSheet: false,
  towerNav: false,
  gameModeControl: false,
  gmTools: false,
  progressionHud: false,
  mergeGmCatalogs: true,
  serverScheduledTheme: false,
};

/** Dungeon floor layout editor — no play HUD rail/hotbar. */
export const DUNGEON_FLOOR_EDITOR_FEATURES: HomeSceneEditorFeatures = {
  ...HOME_SCENE_EDITOR_FEATURES,
  healthPotion: false,
  skillsBar: false,
  skillsModals: false,
  characterSheet: false,
  towerNav: false,
  progressionHud: false,
};

export async function mountHomeSceneEditor(
  root: HTMLElement,
  options: MountHomeSceneEditorOptions,
): Promise<HomeSceneEditorHandle> {
  const { user, features } = options;
  const ui = options.surfaceElement ?? root;
  if (!options.surfaceElement) {
    root.className = options.pageRootClass;
  }
  ui.className = options.pageRootClass;
  const isDungeonFloorEditor = options.pageRootClass.includes("page-dungeon-floor");
  const loadingLabel =
    options.shell === "creator"
      ? "กำลังโหลดฉาก…"
      : options.shell === "tower"
        ? "กำลังโหลดฉากชั้น…"
        : "กำลังโหลดบ้าน…";

  const bootLoader = mountHomeBootLoader(loadingLabel);
  let bootSceneReadyTimer: ReturnType<typeof setTimeout> | null = null;
  let bootLoaderFinished = false;
  const finishBootLoader = () => {
    if (bootLoaderFinished) return;
    bootLoaderFinished = true;
    if (bootSceneReadyTimer) {
      clearTimeout(bootSceneReadyTimer);
      bootSceneReadyTimer = null;
    }
    window.removeEventListener(HomeEvents.RoomSceneReady, onBootRoomSceneReady);
    bootLoader.finish();
  };
  const onBootRoomSceneReady = () => finishBootLoader();

  if (features.mergeGmCatalogs) {
    await hydrateGmMonsterCatalog();
    bootLoader.setProgress(14, "โหลดมอนสเตอร์…");
    await hydrateGmPartCatalog();
    bootLoader.setProgress(22, "โหลดชิ้นส่วน…");
  }
  if (features.cashShop) {
    await hydrateGmShopCatalog();
    bootLoader.setProgress(28, "โหลดร้านค้า…");
    if (features.mergeGmCatalogs) {
      await hydrateDungeonShopConfig();
      bootLoader.setProgress(30, "โหลดร้านมอนสเตอร์…");
    }
  }
  if (options.shell === "home") {
    await hydrateHomeRailMenuLayout();
    bootLoader.setProgress(34, "โหลดเมนู…");
  }
  let homeData = await options.loadRoom();
  bootLoader.setProgress(46, "โหลดห้อง…");
  if (options.fixedGrid) {
    applyGridSizeToRoom(homeData, options.fixedGrid.cols, options.fixedGrid.rows);
  }
  if (features.mergeGmCatalogs) {
    homeData = mergeGmPartCatalogIntoHome(mergeGmMonsterCatalogIntoHome(homeData));
  }
  let economy: HomeEconomy =
    (await loadHomeEconomyFromIdb(user.uid)) ?? createDefaultHomeEconomy();
  bootLoader.setProgress(54, "โหลดกระเป๋า…");
  let skillBar: SkillBarState = features.skillsBar
    ? await loadSkillBar(user.uid)
    : createDefaultSkillBar();
  if (features.skillsBar) {
    const skillLegacy = migrateLegacyGlobalCooldownCards(skillBar, economy);
    skillBar = skillLegacy.bar;
    economy = skillLegacy.economy;
    if (skillLegacy.changed) {
      void saveSkillBar(user.uid, skillBar);
      void saveHomeEconomyToIdb(user.uid, economy);
    }
  }
  let progression: CharacterProgression = features.progressionHud
    ? loadCharacterProgression(user.uid)
    : loadCharacterProgression(user.uid);
  const rankCosmetics = await loadMyRankCosmetics();
  bootLoader.setProgress(62, "โหลดอันดับ…");
  let shadowState: ShadowPlayerState = await loadShadowState(user.uid);
  let petState: PetPlayerState = await loadPetState(user.uid);
  bootLoader.setProgress(72, "เตรียมตัวละคร…");
  let pendingShadowApCardShadowId: string | null = null;
  let currentGameArt: GameArtOptions = options.gameArt;
  const buildEquipmentBonus = () =>
    mergeStatBonuses(
      equipmentStatBonus(loadEquipmentState(user.uid)),
      shadowEquippedStatBonus(equippedShadow(shadowState)),
      rankCosmetics.statBonus,
    );
  let equipmentBonus = buildEquipmentBonus();
  const canGm = features.gameModeControl ? userCanAccessGmMode(user) : false;
  let gameMode: GameAccessMode = features.gameModeControl
    ? loadGameAccessMode(user.uid, canGm)
    : "player";
  let perms = permissionsForGameMode(gameMode, canGm);
  let gameModeControl: GameModeControlHandle | null = null;
  let gmTools: { refresh: () => void; teardown: () => void } | null = null;
  const skillCooldownUntilBySlot: Record<number, number> = {};
  const skillCooldownDurationBySlot: Record<number, number> = {};
  const skillSlotPending: boolean[] = Array.from({ length: SKILL_SLOT_MAX }, () => false);
  let combatActive = false;
  let combatHpOverride: number | null = null;
  const autoItemHotbarCombatAllowed = (): boolean => {
    if (combatActive) return true;
    return combatHpOverride !== null || combatManaOverride !== null;
  };
  let combatHpRegenCarry = 0;
  let combatHpDeltaTimer: ReturnType<typeof setTimeout> | null = null;
  let combatHpRegenTimer: ReturnType<typeof setInterval> | null = null;
  let combatManaOverride: number | null = null;
  let combatManaRegenCarry = 0;
  let sitRestEnabled = false;
  let focusTargetDeltaTimer: ReturnType<typeof setTimeout> | null = null;
  let skillCooldownTimer: ReturnType<typeof setInterval> | null = null;
  let utcClockTimer: ReturnType<typeof setInterval> | null = null;
  let itemHotbarCooldownTimer: ReturnType<typeof setInterval> | null = null;
  /** Manual toggle stays until reload; load always follows server clock. */
  let themeManual = false;
  if (features.serverScheduledTheme) {
    const scheduledTheme = themeIdFromClock(await fetchServerDate());
    if (themeById(homeData.themeId).id !== scheduledTheme) {
      applyThemeToRoom(homeData, scheduledTheme);
    }
  }
  let editMode = false;
  /** Layout snapshot when edit session starts; catalog parts are not reverted on discard. */
  let editSessionSnapshot: HomeLayoutEditSnapshot | null = null;
  let selectedPartId: string | null = null;
  let selectedPlacementId: string | null = null;
  let armedPartId: string | null = null;
  let draggingPartId: string | null = null;
  let shopTab: HomeShopTab = "all";
  /** Multi-select in shop for bulk catalog delete. */
  const catalogBulkSelected = new Set<string>();
  /** Avoid stuffing hundreds of ids into data-delete-ids (truncates in DOM). */
  let pendingCatalogDeleteIds: string[] = [];
  let shopBulkMode = false;
  let currencyShopTab: CurrencyShopTab = "cash";
  let shopPageByTab: ShopPageByTab = defaultShopPageByTab();
  let dungeonShopConfig: DungeonShopConfig = loadDungeonShopConfigCached();
  let shopDetailItemId: string | null = null;
  let shopDetailQty = 1;
  let stashView: StashViewState = defaultStashViewState();
  let game: Phaser.Game | null = null;
  let requestGameViewportSync: () => void = () => {};
  let placeKindPicker: KindPickerHandle | undefined;
  let partKindPicker: KindPickerHandle | undefined;

  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  let economyTimer: ReturnType<typeof setTimeout> | null = null;
  let layerPanelCollapsed = false;

  const syncLayerPanelCollapsedUi = () => {
    const panel = ui.querySelector<HTMLElement>("#home-edit-layer-panel");
    const collapseBtn = ui.querySelector<HTMLButtonElement>("#home-edit-layer-collapse");
    if (!panel) return;
    panel.classList.toggle("home-edit-layer-panel--collapsed", layerPanelCollapsed);
    if (collapseBtn) {
      collapseBtn.setAttribute("aria-expanded", layerPanelCollapsed ? "false" : "true");
      collapseBtn.title = layerPanelCollapsed ? "ขยายแผงชั้น" : "ซ้อนเข้าขอบซ้าย";
      collapseBtn.setAttribute(
        "aria-label",
        layerPanelCollapsed ? "ขยายแผงชั้น" : "ซ้อนแผงชั้นเข้าขอบซ้าย",
      );
    }
  };

  const toggleLayerPanelCollapsed = () => {
    layerPanelCollapsed = !layerPanelCollapsed;
    try {
      sessionStorage.setItem(
        "home-edit-layer-collapsed",
        layerPanelCollapsed ? "1" : "0",
      );
    } catch {
      /* ignore */
    }
    syncLayerPanelCollapsedUi();
  };

  const flushSave = () => {
    void options.saveRoom(homeData).catch((err) => {
      const hint = ui.querySelector<HTMLElement>("#home-mode-hint");
      if (hint) {
        hint.textContent =
          err instanceof Error ? err.message : "บันทึกบ้านไม่สำเร็จ";
      }
    });
  };

  let publicStatsSyncTimer: ReturnType<typeof setTimeout> | null = null;
  const schedulePublicStatsSync = () => {
    if (!isFirebaseConfigured()) return;
    if (publicStatsSyncTimer) clearTimeout(publicStatsSyncTimer);
    publicStatsSyncTimer = setTimeout(() => {
      void syncPlayerPublicStats(user);
    }, 2500);
  };

  const flushEconomy = () => {
    void saveHomeEconomyToIdb(user.uid, economy).catch(() => {
      /* best-effort local wallet */
    });
    schedulePublicStatsSync();
  };

  const slotFocusKind = (): HomePartKind => {
    if (shopTab !== "all") return shopTab;
    if (selectedPartId) {
      const part = homeData.parts.find((p) => p.id === selectedPartId);
      if (part) return normalizePartKind(part.kind);
    }
    return "solid";
  };

  const persistEconomy = () => {
    syncWalletHud();
    syncSlotBar();
    renderPalette();
    if (economyTimer) {
      clearTimeout(economyTimer);
      economyTimer = null;
    }
    flushEconomy();
    window.dispatchEvent(
      new CustomEvent(HomeEvents.EconomyChanged, { detail: { economy } }),
    );
  };

  const structureMount =
    options.shell === "home"
      ? mountHomeStructureFeatures(ui, {
          userId: user.uid,
          getEconomy: () => economy,
          setEconomy: (next) => {
            economy = next;
          },
          getHomeData: () => homeData,
          persistEconomy,
          isEditMode: () => editMode,
          structureRaid: options.structureRaid,
        })
      : null;

  const grantWalletGm = (grant: WalletGrant) => {
    const next = applyWalletGrant(economy, grant);
    if (next === economy) return;
    economy = next;
    persistEconomy();
  };

  const adjustPlacementDepthBias = (placementId: string, delta: number) => {
    window.dispatchEvent(
      new CustomEvent(HomeEvents.AdjustPartDepthBias, {
        detail: { placementId, delta },
      }),
    );
  };

  const nudgeSelectedPlacement = (dx: number, dy: number) => {
    const placementId = selectedPlacementId ?? syncActivePlacementFromScene();
    if (!placementId || !editMode) return;
    const placement = homeData.placements.find((p) => p.id === placementId);
    if (!placement) return;
    const col = placement.gridX + dx;
    const row = placement.gridY + dy;
    if (!canPlaceAt(homeData, placement.partId, col, row, placement.id)) return;
    placement.gridX = col;
    placement.gridY = row;
    persist();
    focusPlacement(placementId);
  };

  const editLayerPanel = bindHomeEditLayerPanel(ui, () => ({
    data: homeData,
    selectedPlacementId,
    onSelectPlacement: (placementId) => focusPlacement(placementId),
    onAdjustDepthBias: adjustPlacementDepthBias,
  }));

  const renderEditLayerPanel = () => {
    const panel = ui.querySelector<HTMLElement>("#home-edit-layer-panel");
    if (panel) panel.hidden = !editMode;
    if (editMode) {
      syncLayerPanelCollapsedUi();
      editLayerPanel.refresh();
    }
  };

  const focusPlacement = (placementId: string) => {
    const placement = homeData.placements.find((p) => p.id === placementId);
    if (!placement) return;
    selectedPlacementId = placementId;
    selectedPartId = placement.partId;
    armedPartId = null;
    getHomeScene()?.selectPlacement(placementId);
    const part = homeData.parts.find((p) => p.id === placement.partId);
    if (part) {
      const kind = normalizePartKind(part.kind);
      getHomeScene()?.setShowSockets(kind === "prop" && armedPartId === selectedPartId);
    }
    gmTools?.refresh();
    renderPalette();
    syncPlaceSizeControls();
    syncDeleteDockState();
    updateModeHint();
    renderEditLayerPanel();
  };

  const clearEditFocus = () => {
    selectedPlacementId = null;
    selectedPartId = null;
    armedPartId = null;
    getHomeScene()?.clearSelectedPlacement();
    getHomeScene()?.clearPlaceGhost();
    renderPalette();
    syncPlaceSizeControls();
    syncDeleteDockState();
    updateModeHint();
    gmTools?.refresh();
    renderEditLayerPanel();
  };

  const syncStageShellWrap = () => {
    const wrap = ui.querySelector<HTMLElement>(".home-stage-wrap");
    if (!wrap) return;
    const url = resolveStageShellUrl(homeData);
    if (url) {
      const display = imageDataUrlForDisplay(url) ?? url;
      wrap.style.backgroundImage = `url(${JSON.stringify(display)})`;
      wrap.style.backgroundSize = "cover";
      wrap.style.backgroundPosition = "center bottom";
      wrap.style.backgroundRepeat = "no-repeat";
    } else {
      wrap.style.backgroundImage = "";
    }
  };

  const applyHomeDataToScene = () => {
    window.dispatchEvent(
      new CustomEvent(HomeEvents.DataChanged, { detail: { data: homeData } }),
    );
    syncStageShellWrap();
    renderPalette();
    syncDeleteDockState();
    syncPlaceSizeControls();
    updateFurnitureCount();
    syncSlotBar();
    syncWalletHud();
    updateModeHint();
    renderEditLayerPanel();
  };

  const persist = (opts?: { catalog?: boolean }) => {
    applyHomeDataToScene();
    if (editMode && !opts?.catalog) return;
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(flushSave, 300);
  };

  const persistCatalog = () => persist({ catalog: true });

  const beginEditSession = () => {
    editSessionSnapshot = captureHomeLayoutEditSnapshot(homeData);
  };

  const commitEditSession = () => {
    editSessionSnapshot = null;
    if (saveTimer) clearTimeout(saveTimer);
    flushSave();
    setEditMode(false);
  };

  const discardEditSession = () => {
    if (!editMode) return;
    if (
      editSessionSnapshot &&
      !window.confirm("ออกจากการแต่งโดยไม่บันทึกการเปลี่ยนแปลง?")
    ) {
      return;
    }
    if (saveTimer) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
    if (editSessionSnapshot) {
      applyHomeLayoutEditSnapshot(homeData, editSessionSnapshot);
    }
    editSessionSnapshot = null;
    armedPartId = null;
    selectedPlacementId = null;
    selectedPartId = null;
    getHomeScene()?.clearSelectedPlacement();
    getHomeScene()?.clearPlaceGhost();
    applyHomeDataToScene();
    closeModal();
    closeShopModal();
    closeSheetPanel();
    setEditMode(false);
  };

  const updateModeHint = () => {
    const hint = ui.querySelector<HTMLElement>("#home-mode-hint");
    if (!hint) return;
    hint.hidden = true;
    hint.textContent = "";
  };

  let editFeedbackTimer: ReturnType<typeof setTimeout> | null = null;

  const activePlacementId = (): string | null => {
    const scene = getHomeScene();
    const id = selectedPlacementId ?? scene?.getSelectedPlacementId() ?? null;
    if (!id || !homeData.placements.some((p) => p.id === id)) return null;
    return id;
  };

  const syncActivePlacementFromScene = (): string | null => {
    const id = activePlacementId();
    if (!id) return null;
    selectedPlacementId = id;
    const placement = homeData.placements.find((p) => p.id === id);
    if (placement) selectedPartId = placement.partId;
    return id;
  };

  const setEditFeedback = (message: string, ms = 3200) => {
    const el = ui.querySelector<HTMLElement>("#home-edit-feedback");
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    if (editFeedbackTimer) clearTimeout(editFeedbackTimer);
    editFeedbackTimer = setTimeout(() => {
      el.hidden = true;
      el.textContent = "";
      editFeedbackTimer = null;
    }, ms);
  };

  const placementCopyState = (
    placementId: string | null,
  ): { canCopy: boolean; title: string } => {
    if (!placementId) {
      return { canCopy: false, title: "เลือกชิ้นที่วางในฉากก่อน" };
    }
    const src = homeData.placements.find((p) => p.id === placementId);
    if (!src) {
      return { canCopy: false, title: "เลือกชิ้นที่วางในฉากก่อน" };
    }
    const part = homeData.parts.find((p) => p.id === src.partId);
    if (!part) {
      return { canCopy: false, title: "เลือกชิ้นที่วางในฉากก่อน" };
    }
    const kind = normalizePartKind(part.kind);
    if (kind === "backdrop") {
      return { canCopy: false, title: "ฉากหลังมีได้ชิ้นเดียว" };
    }
    if (kind === "shell") {
      return { canCopy: false, title: "ฉากรอบกรอบมีได้ชิ้นเดียว" };
    }
    if (kindQuotaRemaining(homeData, kind) <= 0) {
      const quotaTitle =
        kind === "solid"
          ? "บ้านวางในฉากได้ชิ้นเดียว · ลบออกก่อนคัดลอก"
          : `โควต้า${slotLabelTh(kind)}เต็ม · คัดลอกไม่ได้`;
      return { canCopy: false, title: quotaTitle };
    }
    if (!findFirstPlaceCell(homeData, src.partId)) {
      return { canCopy: false, title: "คัดลอกไม่ได้ · ไม่มีช่องว่างบนกริด" };
    }
    return { canCopy: true, title: "คัดลอกชิ้นที่วาง (อีกหนึ่งชุด)" };
  };

  const syncPlaceSizeControls = () => {
    const bar = ui.querySelector<HTMLElement>("#home-edit-selection");
    const wrap = ui.querySelector<HTMLElement>("#home-place-size");
    const scaleInput = ui.querySelector<HTMLInputElement>("#place-scale");
    const floorOpt = ui.querySelector<HTMLElement>("#place-floor-room-wrap");
    const copyBtn = ui.querySelector<HTMLButtonElement>("#home-placement-copy");
    if (!wrap || !scaleInput) return;
    const part = selectedPartId
      ? homeData.parts.find((p) => p.id === selectedPartId)
      : undefined;
    const showPartTools = Boolean(part && editMode);
    const showCatalogOnly = isDungeonFloorEditor && editMode && !part;
    const showBar = showPartTools || showCatalogOnly;
    if (bar) {
      bar.hidden = !showBar;
      bar.classList.toggle("home-edit-selection--catalog-only", showCatalogOnly);
    }
    wrap.hidden = !showPartTools;
    const catalogBtn = ui.querySelector<HTMLElement>("#home-open-parts-catalog");
    if (catalogBtn) catalogBtn.hidden = !isDungeonFloorEditor || !editMode;
    if (!showPartTools) {
      if (copyBtn) copyBtn.disabled = true;
      syncEditQuotaBadge();
      return;
    }
    if (!part) return;
    const placementId = syncActivePlacementFromScene();
    const placement = placementId
      ? homeData.placements.find((p) => p.id === placementId)
      : undefined;
    const scale =
      placement && part.id === placement.partId
        ? effectivePlacementPlaceScale(part, placement)
        : clampPlaceScale(part.placeScale ?? 1);
    scaleInput.value = String(placeScalePercent(scale));
    if (placeKindPicker) {
      const kind = normalizePartKind(part.kind);
      placeKindPicker.setValue(kind);
    }
    if (floorOpt) floorOpt.hidden = normalizePartKind(part.kind) !== "floor";
    if (copyBtn) {
      const placementId = syncActivePlacementFromScene();
      const { canCopy, title } = placementCopyState(placementId);
      copyBtn.disabled = !canCopy;
      copyBtn.title = title;
    }
    syncEditQuotaBadge();
    gmTools?.refresh();
  };

  const resolveEditQuotaKind = (): HomePartKind | null => {
    const focusPartId = armedPartId ?? selectedPartId;
    if (focusPartId) {
      const part = homeData.parts.find((p) => p.id === focusPartId);
      if (part) return normalizePartKind(part.kind);
    }
    if (shopTab !== "all") return shopTab;
    return null;
  };

  const syncEditQuotaBadge = () => {
    const badge = ui.querySelector<HTMLElement>("#home-furniture-badge");
    const wrap = ui.querySelector<HTMLElement>("#home-kind-quota-badge");
    const countEl = ui.querySelector<HTMLElement>("#home-kind-quota-count");
    if (!badge || !wrap || !countEl) return;
    if (!editMode) {
      badge.classList.remove("home-furniture-badge--show-quota");
      return;
    }
    const kind = resolveEditQuotaKind() ?? "solid";
    const used = kindQuotaUsed(homeData, kind);
    const cap = kindQuotaCap(kind);
    const remaining = kindQuotaRemaining(homeData, kind);
    badge.classList.add("home-furniture-badge--show-quota");
    countEl.textContent = `${used}/${cap}`;
    wrap.title = `โควต้า${slotLabelTh(kind)}ในฉาก · เหลือ ${remaining}`;
    wrap.classList.toggle("home-kind-quota-badge--full", remaining <= 0);
  };

  const syncDeleteDockState = () => {
    const deleteBtn = ui.querySelector<HTMLButtonElement>("#home-rail-delete");
    if (!deleteBtn) return;
    const hasPlacement = !!activePlacementId();
    const hasCatalogPart =
      !!selectedPartId && homeData.parts.some((p) => p.id === selectedPartId);
    deleteBtn.disabled = !hasPlacement && !hasCatalogPart;
    deleteBtn.title = hasPlacement
      ? "ลบชิ้นที่วางในห้อง"
      : hasCatalogPart
        ? "ลบชิ้นจากคลัง (ต้องไม่ใช้ในฉาก)"
        : "เลือกลบ";
  };

  const updateFurnitureCount = () => {
    const el = ui.querySelector<HTMLElement>("#home-furniture-count");
    if (el) {
      el.textContent = `${totalCatalogUsed(homeData)} / ${totalCatalogCap(economy)}`;
    }
    syncEditQuotaBadge();
  };

  const dispatchSkillRuntime = () => {
    const detail = buildSkillRuntimePayload(skillBar, economy);
    window.dispatchEvent(
      new CustomEvent(HomeEvents.SkillRuntimeChanged, { detail }),
    );
  };

  const skillCooldownRemainingMs = (slotIndex: number): number => {
    const until = skillCooldownUntilBySlot[slotIndex] ?? 0;
    return Math.max(0, until - Date.now());
  };

  const syncSkillCooldownProgress = () => {
    const bar = ui.querySelector<HTMLElement>("#home-skill-bar");
    if (!bar) return;
    updateSkillBarCooldownDom(
      bar,
      skillCooldownRemainingMs,
      (index) => skillCooldownDurationBySlot[index] ?? 0,
    );
  };

  const applySkillSlotCooldown = (slotIndex: number): void => {
    const skillId = skillBar.slots[slotIndex];
    const used = skillId ? skillById(skillBar, skillId) : undefined;
    const cd = used ? skillEffectiveCooldownMs(used) : skillCooldownMs(0);
    bumpSlotCooldownRecord(
      skillCooldownUntilBySlot,
      skillCooldownDurationBySlot,
      slotIndex,
      Date.now() + cd,
      cd,
    );
  };

  const useSkillSlot = (slotIndex: number, skillId: string): void => {
    if (editMode) return;
    if (sitRestEnabled) setSitRestEnabled(false);
    if (skillSlotPending[slotIndex]) return;
    if (skillCooldownRemainingMs(slotIndex) > 0) return;
    if (features.progressionHud) {
      const { current } = combatManaSnapshot();
      if (current < SKILL_MANA_COST) return;
    }
    skillSlotPending[slotIndex] = true;
    window.dispatchEvent(
      new CustomEvent(HomeEvents.UseSkill, {
        detail: { skillId, slotIndex },
      }),
    );
  };

  const onSkillUseRejected = (event: Event) => {
    const { slotIndex } = (event as CustomEvent<TowerSkillUseRejectedPayload>).detail;
    if (!Number.isFinite(slotIndex) || slotIndex < 0 || slotIndex >= SKILL_SLOT_MAX) return;
    skillSlotPending[slotIndex] = false;
  };

  const onSkillUseAccepted = (event: Event) => {
    const { slotIndex, chainGcdMs } = (event as CustomEvent<TowerSkillUseAcceptedPayload>).detail;
    if (!Number.isFinite(slotIndex) || slotIndex < 0 || slotIndex >= SKILL_SLOT_MAX) return;
    skillSlotPending[slotIndex] = false;
    if (features.progressionHud) {
      spendCombatMana(SKILL_MANA_COST);
    }
    applySkillSlotCooldown(slotIndex);
    if (Number.isFinite(chainGcdMs) && chainGcdMs > 0) {
      applySkillChainGcdToOtherSlotsRecord(
        skillCooldownUntilBySlot,
        skillCooldownDurationBySlot,
        slotIndex,
        chainGcdMs,
      );
    }
    syncSkillBarHud();
  };

  const tryAutoSkillSlots = (): void => {
    if (editMode) return;
    if (sitRestEnabled) return;
    if (!skillBar.masterAuto) return;
    if (!combatActive && !autoItemHotbarCombatAllowed()) return;
    for (let index = 0; index < SKILL_SLOT_MAX; index += 1) {
      if (!skillBar.slotAuto[index]) continue;
      const skillId = skillBar.slots[index];
      if (!skillId) continue;
      if (skillCooldownRemainingMs(index) > 0) continue;
      useSkillSlot(index, skillId);
    }
  };

  const syncItemHotbarHud = () => {
    syncItemHotbar(ui, economy, "home", {
      itemUseCooldown: !itemHotbarUseReady(),
    });
    syncItemHotbarCooldownState(ui, "home");
  };

  const syncSkillBarHud = () => {
    syncItemHotbarHud();
    const bar = ui.querySelector<HTMLElement>("#home-skill-bar");
    if (!bar) return;
    bar.innerHTML = renderPlaySkillBarSlotsHtml({
      skillBar,
      unlocked: economy.skillSlotsUnlocked,
      skillCooldownRemainingMs,
      lockedTitle: "ล็อก · ซื้อบัตรที่ร้าน",
    });
    syncPlaySkillBarMasterAutoUi(ui, skillBar, economy.skillSlotsUnlocked);
    syncPlaySkillBarSitRestUi(ui, sitRestEnabled);
    syncSkillCooldownProgress();
  };

  const dungeonMonsterShopContext = () =>
    features.mergeGmCatalogs
      ? { shop: dungeonShopConfig, catalog: loadGmMonsterCatalog() }
      : undefined;

  const syncCashShopUi = () => {
    if (!features.cashShop) return;
    shopPageByTab = refreshCurrencyShopPanelDom(
      ui,
      economy,
      shopPageByTab,
      dungeonMonsterShopContext(),
    );
    refreshShopDetailIfOpen();
  };

  const shopDetailModalEl = () => ui.querySelector<HTMLElement>("#home-shop-detail-modal");

  const closeShopDetailModal = () => {
    const modal = shopDetailModalEl();
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    shopDetailItemId = null;
    shopDetailQty = 1;
  };

  const refreshShopDetailIfOpen = () => {
    if (!shopDetailItemId) return;
    const item = getGmShopItemById(shopDetailItemId);
    if (!item) {
      closeShopDetailModal();
      return;
    }
    const kindSelect = ui.querySelector<HTMLSelectElement>("#home-shop-detail-kind");
    const catalogKind = gemKindFromSelect(kindSelect ?? null, slotFocusKind());
    const affordable = maxAffordableShopQuantity(economy, item);
    shopDetailQty =
      affordable > 0
        ? Math.min(Math.max(1, shopDetailQty), affordable)
        : Math.max(1, shopDetailQty);
    syncShopDetailDom(ui, {
      item,
      economy,
      quantity: shopDetailQty,
      catalogPartKind: catalogKind,
    });
  };

  const openShopItemDetail = (itemId: string) => {
    const item = getGmShopItemById(itemId);
    if (!item) return;
    shopDetailItemId = itemId;
    shopDetailQty = 1;
    const modal = shopDetailModalEl();
    if (!modal) return;
    syncShopDetailDom(ui, {
      item,
      economy,
      quantity: shopDetailQty,
      catalogPartKind: item.catalogPartKind ?? slotFocusKind(),
    });
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
  };

  const applyShopPurchaseEffects = (
    item: ReturnType<typeof getGmShopItemById>,
    quantity = 1,
  ) => {
    if (!item) return;
    if (item.fulfillment === "dungeon_floor_slot") {
      let meta = loadDungeonMeta(user.uid);
      const next = grantDungeonFloorSlots(meta, quantity);
      if (next) {
        meta = next;
        saveDungeonMeta(user.uid, meta);
      }
    }
    if (
      item.fulfillment === "skill_slot_card" ||
      item.fulfillment === "skill_cooldown_card" ||
      item.fulfillment === "skill_ap_card" ||
      item.fulfillment === "skill_buff_duration_card" ||
      item.fulfillment === "skill_aoe_range_card" ||
      item.fulfillment === "skill_library_slot_card" ||
      item.fulfillment === "skill_create_card" ||
      item.fulfillment === "shadow_create_card" ||
      item.fulfillment === "shadow_ap_card"
    ) {
      dispatchSkillRuntime();
    }
    if (item.fulfillment === "catalog_slot_pack") {
      syncSlotBar();
      renderPalette();
    }
  };

  const syncGameModeUi = () => {
    perms = permissionsForGameMode(gameMode, canGm);
    ui.classList.toggle("home-shell--player-mode", perms.mode === "player");
    ui.classList.toggle("home-shell--gm-mode", perms.mode === "gm");
    const enterEdit = ui.querySelector<HTMLElement>("#home-enter-edit");
    if (enterEdit) enterEdit.hidden = !perms.canEditHomeLayout;
    const gmHost = ui.querySelector<HTMLElement>("#home-gm-tools-host");
    if (gmHost) {
      const gmDrawerOpen = perms.mode === "gm" && perms.canGrantExp;
      gmHost.classList.toggle("home-gm-tools-host--open", gmDrawerOpen);
      gmHost.setAttribute("aria-hidden", gmDrawerOpen ? "false" : "true");
    }
    gmTools?.refresh();
    requestGameViewportSync();
    if (!perms.canEditHomeLayout && editMode) {
      setEditMode(false);
    }
  };

  const setCurrencyShopTab = (tab: CurrencyShopTab) => {
    currencyShopTab = tab;
    ui.querySelectorAll<HTMLElement>("[data-currency-shop-tab]").forEach((btn) => {
      const id = btn.dataset.currencyShopTab as CurrencyShopTab | undefined;
      const active = id === tab;
      btn.classList.toggle("home-currency-shop-tab--active", active);
      btn.setAttribute("aria-selected", active ? "true" : "false");
    });
    ui.querySelectorAll<HTMLElement>("[data-currency-shop-panel]").forEach((panel) => {
      const id = panel.dataset.currencyShopPanel;
      panel.hidden = id !== tab;
    });
    syncCashShopUi();
  };

  const openCurrencyShopModal = (tab: CurrencyShopTab = currencyShopTab) => {
    if (!perms.canUseCashShop) return;
    closeModal();
    closeShopModal();
    closeStashModal();
    closeSheetPanel();
    const modal = ui.querySelector<HTMLElement>("#home-currency-shop-modal");
    if (!modal) return;
    setCurrencyShopTab(tab);
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    ui.classList.add("home-cash-shop-open");
  };

  const closeCurrencyShopModal = () => {
    closeShopDetailModal();
    const modal = ui.querySelector<HTMLElement>("#home-currency-shop-modal");
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    ui.classList.remove("home-cash-shop-open");
  };

  const stashModalEl = () => ui.querySelector<HTMLElement>("#home-stash-modal");

  const stashDetailModalEl = () =>
    ui.querySelector<HTMLElement>("#home-stash-detail-modal");

  const closeStashDetailModal = () => {
    stashView = { ...stashView, detailItemId: null, discardQty: 1 };
    const detail = stashDetailModalEl();
    if (detail) {
      detail.hidden = true;
      detail.setAttribute("aria-hidden", "true");
    }
  };

  const refreshStashUi = () => {
    refreshStashModalDom(ui, economy, stashView);
  };

  const closeStashModal = () => {
    closeStashDetailModal();
    const modal = stashModalEl();
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    ui.classList.remove("home-stash-open");
    document.body.classList.remove("home-stash-open");
    stashView = defaultStashViewState();
  };

  const openStashModal = () => {
    if (!features.walletHud) return;
    closeModal();
    closeShopModal();
    closeCurrencyShopModal();
    closeSheetPanel();
    closeCharacterSheetModal();
    closeSkillsModal();
    const modal = stashModalEl();
    if (!modal) return;
    stashView = { ...defaultStashViewState(), sort: stashView.sort };
    refreshStashUi();
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    ui.classList.add("home-stash-open");
    document.body.classList.add("home-stash-open");
  };

  const openStashItemDetail = (itemId: StashItemId) => {
    const slot = getStashSlotById(economy, itemId);
    if (!slot) return;
    stashView = {
      ...stashView,
      detailItemId: itemId,
      discardQty: slot.count > 0 ? 1 : 1,
    };
    refreshStashUi();
  };

  const characterModalEl = () => ui.querySelector<HTMLElement>("#home-character-modal");
  const settingsModalEl = () => ui.querySelector<HTMLElement>("#home-settings-modal");

  const closeSettingsModal = () => {
    const modal = settingsModalEl();
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    document.body.classList.remove("home-settings-open");
  };

  const openSettingsModal = () => {
    closeModal();
    closeShopModal();
    closeCurrencyShopModal();
    closeSheetPanel();
    closeCharacterSheetModal();
    closeSkillsModal();
    const modal = settingsModalEl();
    if (!modal) return;
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    document.body.classList.add("home-settings-open");
    requestAnimationFrame(() => {
      modal
        .querySelector<HTMLButtonElement>("[data-close-home-settings].btn-icon")
        ?.focus({ preventScroll: true });
    });
  };

  const closeCharacterSheetModal = () => {
    const modal = characterModalEl();
    if (!modal || modal.hidden) return;
    characterSheetModal?.close();
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    ui.classList.remove("home-character-sheet-open");
    document.body.classList.remove("home-character-sheet-open");
  };

  const skillsModalEl = () => ui.querySelector<HTMLElement>("#home-skills-modal");

  const closeSkillsModal = () => {
    const modal = skillsModalEl();
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    ui.classList.remove("home-skills-modal-open");
    document.body.classList.remove("home-skills-modal-open");
  };

  const openSkillsModal = () => {
    closeModal();
    closeShopModal();
    closeCurrencyShopModal();
    closeSheetPanel();
    closeCharacterSheetModal();
    closeSettingsModal();
    const modal = skillsModalEl();
    if (!modal) return;
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    ui.classList.add("home-skills-modal-open");
    document.body.classList.add("home-skills-modal-open");
    skillsPanelHandle?.refresh();
    requestAnimationFrame(() => {
      modal
        .querySelector<HTMLButtonElement>("[data-close-skills-modal].btn-icon")
        ?.focus({ preventScroll: true });
    });
  };

  const openCharacterSheetModal = () => {
    closeModal();
    closeShopModal();
    closeCurrencyShopModal();
    closeSheetPanel();
    closeSkillsModal();
    closeSettingsModal();
    const modal = characterModalEl();
    if (!modal) return;
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    ui.classList.add("home-character-sheet-open");
    document.body.classList.add("home-character-sheet-open");
    characterSheetModal?.open();
    requestAnimationFrame(() => {
      modal
        .querySelector<HTMLButtonElement>("[data-close-character-sheet].btn-icon")
        ?.focus({ preventScroll: true });
    });
  };

  const syncProgressionHud = () => {
    const sub = ui.querySelector<HTMLElement>("#home-hud-sub");
    if (sub) {
      sub.textContent = features.progressionHud ? `Level ${progression.level}` : "";
      sub.hidden = !features.progressionHud;
    }
  };

  const persistProgression = (levelsGained = 0) => {
    saveCharacterProgression(user.uid, progression);
    syncProgressionHud();
    window.dispatchEvent(
      new CustomEvent(HomeEvents.ProgressionChanged, {
        detail: { progression, levelsGained },
      }),
    );
  };

  const applyExpGrant = (amount: number) => {
    const result = grantExp(progression, amount);
    progression = result.progression;
    persistProgression(result.levelsGained);
  };

  const syncWalletHud = () => {
    const coins = ui.querySelector<HTMLElement>("#home-wallet-coins");
    const gems = ui.querySelector<HTMLElement>("#home-wallet-gems");
    const cash = ui.querySelector<HTMLElement>("#home-wallet-cash");
    if (coins) coins.textContent = economy.wallet.coins.toLocaleString("en-US");
    if (gems) gems.textContent = economy.wallet.gems.toLocaleString("en-US");
    if (cash) cash.textContent = economy.wallet.cash.toLocaleString("en-US");
    syncSkillBarHud();
    syncCashShopUi();
    const stash = stashModalEl();
    if (stash && !stash.hidden) {
      refreshStashUi();
    }
  };

  const syncCombatHpHud = (current: number, max: number, damageTaken = 0) => {
    if (!features.progressionHud) return;
    const pill = ui.querySelector<HTMLElement>("#home-combat-hp-pill");
    const value = ui.querySelector<HTMLElement>("#home-combat-hp-value");
    const fill = ui.querySelector<HTMLElement>("#home-combat-hp-fill");
    const delta = ui.querySelector<HTMLElement>("#home-combat-hp-delta");
    if (!pill || !value || !fill) return;
    const safeMax = Math.max(1, max);
    const safeCurrent = Math.max(0, Math.min(safeMax, current));
    const ratio = safeCurrent / safeMax;
    value.textContent = `${safeCurrent.toLocaleString("en-US")}/${safeMax.toLocaleString("en-US")}`;
    fill.style.width = `${(ratio * 100).toFixed(1)}%`;
    pill.classList.toggle("home-combat-hp-pill--low", ratio <= 0.25 && safeCurrent > 0);
    pill.classList.toggle("home-combat-hp-pill--empty", safeCurrent <= 0);
    if (damageTaken > 0) {
      pill.classList.remove("home-combat-hp-pill--damage");
      void pill.offsetWidth;
      pill.classList.add("home-combat-hp-pill--damage");
      if (delta) {
        delta.hidden = false;
        delta.textContent = `-${damageTaken.toLocaleString("en-US")}`;
        delta.classList.remove("home-combat-hp-delta--fade");
      }
      if (combatHpDeltaTimer) clearTimeout(combatHpDeltaTimer);
      combatHpDeltaTimer = setTimeout(() => {
        delta?.classList.add("home-combat-hp-delta--fade");
        combatHpDeltaTimer = setTimeout(() => {
          if (delta) {
            delta.hidden = true;
            delta.textContent = "";
            delta.classList.remove("home-combat-hp-delta--fade");
          }
          pill?.classList.remove("home-combat-hp-pill--damage");
          combatHpDeltaTimer = null;
        }, 280);
      }, 700);
    }
  };

  const combatHpSnapshot = () => {
    const ratings = playerRatings(progression, equipmentBonus);
    const max = playerCombatMaxHp(ratings);
    const raw =
      combatHpOverride !== null
        ? combatHpOverride
        : economy.combatHpCurrent !== null
          ? economy.combatHpCurrent
          : max;
    const current = Math.max(0, Math.min(max, raw));
    return { current, max, ratings };
  };

  const commitCombatHp = (current: number) => {
    const ratings = playerRatings(progression, equipmentBonus);
    const max = playerCombatMaxHp(ratings);
    const clamped = Math.max(0, Math.min(max, Math.floor(current)));
    if (!shouldPersistCombatHp(economy, clamped, max)) return;
    const next = setCombatHpCurrent(economy, clamped);
    if (next === economy) return;
    economy = next;
    persistEconomy();
  };

  const syncCombatPowerHud = () => {
    if (!features.progressionHud) return;
    const value = ui.querySelector<HTMLElement>("#home-combat-power-value");
    if (!value) return;
    const ratings = playerRatings(progression, equipmentBonus);
    const max = playerCombatMaxHp(ratings);
    value.textContent = characterCombatPower(ratings, max).toLocaleString("en-US");
  };

  const syncCombatHitBestHud = () => {
    if (!features.progressionHud) return;
    const value = ui.querySelector<HTMLElement>("#home-combat-hit-best-value");
    if (!value) return;
    value.textContent = loadCombatBestKillStreak(user.uid).toLocaleString("en-US");
  };

  const syncCombatHpFromStats = () => {
    if (!features.progressionHud) return;
    const { current, max } = combatHpSnapshot();
    if (combatHpOverride === null) {
      commitCombatHp(current);
    }
    syncCombatHpHud(current, max, 0);
    syncCombatPowerHud();
    syncCombatHitBestHud();
    syncCombatManaFromStats();
  };

  const setSitRestEnabled = (enabled: boolean) => {
    if (sitRestEnabled === enabled) return;
    sitRestEnabled = enabled;
    emitCombatSitRestChanged(enabled);
    syncPlaySkillBarSitRestUi(ui, enabled);
  };

  const sitRestRegenMultiplier = (): number =>
    sitRestEnabled ? COMBAT_SIT_REST_REGEN_MULT : 1;

  const tickCombatHpRegen = () => {
    if (!features.progressionHud) return;
    if (combatHpOverride !== null) return;
    const { current, max, ratings } = combatHpSnapshot();
    if (current >= max) return;
    const result = applyHpRegenTick(
      current,
      max,
      combatRatingsHpRegenPerSec(ratings) * sitRestRegenMultiplier(),
      combatHpRegenCarry,
    );
    combatHpRegenCarry = result.carry;
    if (result.healed <= 0) return;
    commitCombatHp(result.current);
    syncCombatHpHud(result.current, max, 0);
    tryAutoItemHotbar();
  };

  const combatManaSnapshot = () => {
    const ratings = playerRatings(progression, equipmentBonus);
    const max = playerCombatMaxMana(ratings);
    const raw =
      combatManaOverride !== null
        ? combatManaOverride
        : economy.combatManaCurrent !== null
          ? economy.combatManaCurrent
          : max;
    const current = Math.max(0, Math.min(max, raw));
    return { current, max, ratings };
  };

  const commitCombatMana = (current: number) => {
    const ratings = playerRatings(progression, equipmentBonus);
    const max = playerCombatMaxMana(ratings);
    const clamped = Math.max(0, Math.min(max, Math.floor(current)));
    if (!shouldPersistCombatMana(economy, clamped, max)) return;
    const next = setCombatManaCurrent(economy, clamped);
    if (next === economy) return;
    economy = next;
    persistEconomy();
  };

  const syncCombatManaHud = (current: number, max: number) => {
    if (!features.progressionHud) return;
    const pill = ui.querySelector<HTMLElement>("#home-combat-mana-pill");
    const value = ui.querySelector<HTMLElement>("#home-combat-mana-value");
    const fill = ui.querySelector<HTMLElement>("#home-combat-mana-fill");
    if (!pill || !value || !fill) return;
    const safeMax = Math.max(1, max);
    const safeCurrent = Math.max(0, Math.min(safeMax, current));
    const ratio = safeCurrent / safeMax;
    value.textContent = `${safeCurrent.toLocaleString("en-US")}/${safeMax.toLocaleString("en-US")}`;
    fill.style.width = `${(ratio * 100).toFixed(1)}%`;
    pill.classList.toggle("home-combat-mana-pill--low", ratio <= 0.25 && safeCurrent > 0);
    pill.classList.toggle("home-combat-mana-pill--empty", safeCurrent < SKILL_MANA_COST);
  };

  const syncCombatManaFromStats = () => {
    if (!features.progressionHud) return;
    const { current, max } = combatManaSnapshot();
    if (combatManaOverride === null) {
      commitCombatMana(current);
    }
    syncCombatManaHud(current, max);
  };

  const tickCombatManaRegen = () => {
    if (!features.progressionHud) return;
    if (combatManaOverride !== null) return;
    const { current, max } = combatManaSnapshot();
    if (current >= max) return;
    const result = applyHpRegenTick(
      current,
      max,
      combatManaRegenPerSec() * sitRestRegenMultiplier(),
      combatManaRegenCarry,
    );
    combatManaRegenCarry = result.carry;
    if (result.healed <= 0) return;
    commitCombatMana(result.current);
    syncCombatManaHud(result.current, max);
    tryAutoItemHotbar();
  };

  const spendCombatMana = (amount: number): boolean => {
    const { current, max } = combatManaSnapshot();
    if (current < amount) return false;
    const next = current - amount;
    combatManaOverride = null;
    commitCombatMana(next);
    syncCombatManaHud(next, max);
    tryAutoItemHotbar();
    return true;
  };

  const resetCombatHpOverride = () => {
    combatHpOverride = null;
    syncCombatHpFromStats();
  };

  const resetCombatManaOverride = () => {
    combatManaOverride = null;
    syncCombatManaFromStats();
  };

  const clearTowerFocusHud = () => {
    const host = ui.querySelector<HTMLElement>("#home-hud-focus");
    if (host) host.hidden = true;
  };

  const syncTowerFocusHud = (detail: TowerFocusTargetChangedPayload) => {
    const host = ui.querySelector<HTMLElement>("#home-hud-focus");
    const img = ui.querySelector<HTMLImageElement>("#home-hud-focus-img");
    const name = ui.querySelector<HTMLElement>("#home-hud-focus-name");
    const hpText = ui.querySelector<HTMLElement>("#home-hud-focus-hp");
    const fill = ui.querySelector<HTMLElement>("#home-hud-focus-fill");
    const delta = ui.querySelector<HTMLElement>("#home-hud-focus-delta");
    if (!host || !img || !name || !hpText || !fill) return;

    if (!detail.target) {
      host.hidden = true;
      return;
    }

    const { target, damageTaken } = detail;
    const safeMax = Math.max(1, target.maxHp);
    const safeCurrent = Math.max(0, Math.min(safeMax, target.currentHp));
    const ratio = safeCurrent / safeMax;

    host.hidden = false;
    img.src = target.imageDataUrl || HOME_ASSETS.shopTabs.monster;
    name.textContent = target.name;
    hpText.textContent = `${safeCurrent.toLocaleString("en-US")} / ${safeMax.toLocaleString("en-US")}`;
    fill.style.width = `${(ratio * 100).toFixed(1)}%`;
    host.classList.toggle("home-hud-focus--low", ratio <= 0.25 && safeCurrent > 0);

    if (damageTaken > 0) {
      host.classList.remove("home-hud-focus--hit");
      void host.offsetWidth;
      host.classList.add("home-hud-focus--hit");
      if (delta) {
        delta.hidden = false;
        delta.textContent = `-${damageTaken.toLocaleString("en-US")}`;
        delta.classList.remove("home-hud-focus-delta--fade");
      }
      if (focusTargetDeltaTimer) clearTimeout(focusTargetDeltaTimer);
      focusTargetDeltaTimer = setTimeout(() => {
        delta?.classList.add("home-hud-focus-delta--fade");
        focusTargetDeltaTimer = setTimeout(() => {
          if (delta) {
            delta.hidden = true;
            delta.textContent = "";
            delta.classList.remove("home-hud-focus-delta--fade");
          }
          host.classList.remove("home-hud-focus--hit");
          focusTargetDeltaTimer = null;
        }, 280);
      }, 700);
    }
  };

  const syncSlotBar = () => {
    const kind = slotFocusKind();
    const used = catalogSlotUsed(homeData, kind);
    const cap = catalogSlotCap(economy, kind);
    const remaining = catalogSlotRemaining(homeData, economy, kind);
    const label = ui.querySelector<HTMLElement>("#home-slot-label");
    const storeBtn = ui.querySelector<HTMLButtonElement>("#home-slot-open-store");
    if (label) {
      label.textContent = `${slotLabelTh(kind)} ${used}/${cap}`;
      label.title =
        shopTab === "all"
          ? `โฟกัสหมวด ${slotLabelTh(kind)} · คลังรวม ${totalCatalogUsed(homeData)}/${totalCatalogCap(economy)}`
          : `ช่องคลัง${slotLabelTh(kind)}`;
      label.classList.toggle("home-slot-label--full", remaining === 0);
    }
    if (storeBtn) {
      storeBtn.title =
        remaining === 0
          ? `ช่อง${slotLabelTh(kind)}เต็ม · ซื้อเพชรที่ร้าน`
          : `ซื้อช่องเพชรที่ร้าน (+${CATALOG_SLOT_PACK_SIZE} · ${CATALOG_SLOT_PACK_GEM_COST})`;
      storeBtn.classList.toggle("home-slot-buy--urgent", remaining === 0);
    }
  };

  const catalogPartUsage = (partId: string): { inUse: boolean; reasons: string[] } => {
    const reasons: string[] = [];
    const placementCount = homeData.placements.filter((p) => p.partId === partId).length;
    if (placementCount > 0) reasons.push(`วางในฉาก ${placementCount} จุด`);
    if (homeData.backdropPartId === partId) reasons.push("ใช้เป็นฉากหลัง");
    if (homeData.shellPartId === partId) reasons.push("ใช้เป็นฉากรอบกรอบ");
    if (homeData.floorPartId === partId) reasons.push("ใช้เป็นพื้นห้อง");
    return { inUse: reasons.length > 0, reasons };
  };

  const canDeleteCatalogPart = (partId: string): boolean =>
    !catalogPartUsage(partId).inUse;

  const pruneCatalogBulkSelection = () => {
    for (const id of [...catalogBulkSelected]) {
      if (!homeData.parts.some((p) => p.id === id)) catalogBulkSelected.delete(id);
    }
  };

  const visibleCatalogPartIds = (): string[] =>
    homeData.parts
      .filter(
        (part) => shopTab === "all" || normalizePartKind(part.kind) === shopTab,
      )
      .map((part) => part.id);

  const syncBulkDeleteControls = () => {
    pruneCatalogBulkSelection();
    const bulkBar = ui.querySelector<HTMLElement>("#home-shop-bulk-bar");
    if (bulkBar) bulkBar.hidden = !shopBulkMode;
    const deleteBtn = ui.querySelector<HTMLButtonElement>("#home-shop-delete-selected");
    const selectAllBtn = ui.querySelector<HTMLButtonElement>("#home-shop-select-all");
    const bulkToggle = ui.querySelector<HTMLButtonElement>("#home-shop-bulk-toggle");
    const dialog = ui.querySelector<HTMLElement>(".home-shop-dialog");
    dialog?.classList.toggle("home-shop-dialog--bulk", shopBulkMode);
    if (bulkToggle) {
      bulkToggle.setAttribute("aria-pressed", shopBulkMode ? "true" : "false");
      bulkToggle.classList.toggle("home-shop-tool--active", shopBulkMode);
      bulkToggle.title = shopBulkMode ? "ปิดโหมดเลือกหลายรายการ" : "เลือกหลายรายการ";
      bulkToggle.setAttribute(
        "aria-label",
        shopBulkMode ? "ปิดโหมดเลือกหลายรายการ" : "เลือกหลายรายการ",
      );
    }
    const count = catalogBulkSelected.size;
    const visible = visibleCatalogPartIds();
    const allVisibleSelected =
      visible.length > 0 && visible.every((id) => catalogBulkSelected.has(id));
    if (deleteBtn) {
      deleteBtn.disabled = count === 0;
      deleteBtn.textContent = count > 0 ? `ลบที่เลือก (${count})` : "ลบที่เลือก";
    }
    if (selectAllBtn) {
      selectAllBtn.disabled = visible.length === 0;
      selectAllBtn.textContent = allVisibleSelected ? "ยกเลิกเลือก" : "เลือกทั้งหมด";
      selectAllBtn.title =
        shopTab === "all"
          ? allVisibleSelected
            ? "ยกเลิกเลือกทุกชิ้นในรายการ"
            : "เลือกทุกชิ้นในรายการ"
          : allVisibleSelected
            ? `ยกเลิกเลือกทุกชิ้นในแท็บ${slotLabelTh(shopTab)}`
            : `เลือกทุกชิ้นในแท็บ${slotLabelTh(shopTab)}`;
    }
  };

  const closeDeleteConfirmModal = () => {
    const modal = ui.querySelector<HTMLElement>("#home-delete-modal");
    if (!modal || modal.hasAttribute("hidden")) return;
    modal.setAttribute("hidden", "");
    modal.setAttribute("aria-hidden", "true");
    ui.classList.remove("home-delete-open");
    pendingCatalogDeleteIds = [];
  };

  const executeCatalogDeletes = (partIds: string[]) => {
    const deletable = partIds.filter((id) => canDeleteCatalogPart(id));
    if (deletable.length === 0) return;
    const drop = new Set(deletable);
    homeData.parts = homeData.parts.filter((p) => !drop.has(p.id));
    for (const id of deletable) catalogBulkSelected.delete(id);
    if (armedPartId && drop.has(armedPartId)) armedPartId = null;
    if (selectedPartId && drop.has(selectedPartId)) selectedPartId = null;
    selectedPlacementId = null;
    getHomeScene()?.clearSelectedPlacement();
    closeDeleteConfirmModal();
    persistCatalog();
    const hint = ui.querySelector<HTMLElement>("#home-mode-hint");
    if (hint) hint.textContent = `ลบออกจากคลัง ${deletable.length} ชิ้น`;
  };

  const openDeleteConfirmModal = (partIds: string[]) => {
    const unique = [...new Set(partIds)].filter((id) =>
      homeData.parts.some((p) => p.id === id),
    );
    if (unique.length === 0) return;

    const modal = ui.querySelector<HTMLElement>("#home-delete-modal");
    const listEl = ui.querySelector<HTMLElement>("#home-delete-list");
    const confirmBtn = ui.querySelector<HTMLButtonElement>("#home-delete-confirm");
    const blockedHint = ui.querySelector<HTMLElement>("#home-delete-blocked-hint");
    if (!modal || !listEl || !confirmBtn) return;

    listEl.replaceChildren();
    let deletableCount = 0;
    let blockedCount = 0;

    for (const id of unique) {
      const part = homeData.parts.find((p) => p.id === id);
      if (!part) continue;
      const usage = catalogPartUsage(id);
      const row = document.createElement("li");
      row.className = "home-delete-row";
      if (usage.inUse) {
        blockedCount += 1;
        row.classList.add("home-delete-row--blocked");
      } else {
        deletableCount += 1;
      }
      const name = document.createElement("span");
      name.className = "home-delete-row-name";
      name.textContent = part.name;
      const meta = document.createElement("span");
      meta.className = "home-delete-row-meta";
      meta.textContent = usage.inUse
        ? usage.reasons.join(" · ")
        : `${part.cellsW}×${part.cellsH} · ${kindPreset(normalizePartKind(part.kind)).tabLabelTh}`;
      row.append(name, meta);
      listEl.append(row);
    }

    if (blockedHint) {
      if (blockedCount > 0) {
        blockedHint.hidden = false;
        blockedHint.textContent =
          blockedCount === unique.length
            ? "ชิ้นที่เลือกยังใช้ในฉากอยู่ — เอาออกจากฉากก่อน แล้วค่อยลบจากคลัง"
            : `มี ${blockedCount} ชิ้นยังใช้ในฉาก · จะลบได้เฉพาะ ${deletableCount} ชิ้นที่ไม่ได้ใช้`;
      } else {
        blockedHint.hidden = true;
        blockedHint.textContent = "";
      }
    }

    pendingCatalogDeleteIds = unique.filter((id) => canDeleteCatalogPart(id));
    confirmBtn.disabled = pendingCatalogDeleteIds.length === 0;

    modal.removeAttribute("hidden");
    modal.setAttribute("aria-hidden", "false");
    ui.classList.add("home-delete-open");
    requestAnimationFrame(() => {
      confirmBtn.focus({ preventScroll: true });
    });
  };

  const deleteCatalogPart = (partId: string) => {
    openDeleteConfirmModal([partId]);
  };

  const closeShopModal = () => {
    const shopRoot = ui.querySelector<HTMLElement>("#home-shop-modal");
    if (!shopRoot || shopRoot.hasAttribute("hidden")) return;
    shopRoot.setAttribute("hidden", "");
    shopRoot.setAttribute("aria-hidden", "true");
    ui.classList.remove("home-shop-open");
    shopBulkMode = false;
    catalogBulkSelected.clear();
    syncBulkDeleteControls();
    closeDeleteConfirmModal();
  };

  const setShopBulkMode = (enabled: boolean) => {
    shopBulkMode = enabled;
    if (!enabled) catalogBulkSelected.clear();
    syncBulkDeleteControls();
    renderPalette();
  };

  const closeSheetPanel = () => {
    const panelRoot = ui.querySelector<HTMLElement>("#home-sheet-panel");
    if (!panelRoot || panelRoot.hasAttribute("hidden")) return;
    panelRoot.setAttribute("hidden", "");
    panelRoot.setAttribute("aria-hidden", "true");
    ui.classList.remove("home-sheet-panel-open");
    document.body.classList.remove("home-sheet-panel-open");
  };

  const sheetUploadPreset = (): HomeSheetUploadPreset => {
    if (shopTab === "floor") return "floor_tile";
    if (shopTab === "shell") return "shell";
    return "default";
  };

  const syncSheetPanelChrome = (preset: HomeSheetUploadPreset) => {
    const lede = ui.querySelector<HTMLElement>("#home-sheet-panel-lede");
    if (!lede) return;
    if (preset === "floor_tile") {
      lede.textContent =
        "อัปโหลดพื้นแบบสี่เหลี่ยมจัตุรัสเพื่อนำไปวาง · วางแล้วอยู่ชั้นหลัง (ใต้บ้านและของตกแต่ง)";
      lede.hidden = false;
    } else if (preset === "shell") {
      lede.textContent =
        "อัปโหลดภาพพื้นหลังรอบกรอบนอกสเตจ · แสดงนอกกรอบเล่น (ไม่ใช่ภาพในกริด)";
      lede.hidden = false;
    } else {
      lede.textContent = "";
      lede.hidden = true;
    }
  };

  const openSheetPanel = (opts?: { resetWizard?: boolean }) => {
    if (!editMode) setEditMode(true);
    closeModal();
    closeShopModal();
    const preset = sheetUploadPreset();
    if (opts?.resetWizard !== false) sheetCropHandle?.resetWizard();
    sheetCropHandle?.setUploadPreset(preset);
    syncSheetPanelChrome(preset);
    const panelRoot = ui.querySelector<HTMLElement>("#home-sheet-panel");
    if (!panelRoot) return;
    panelRoot.removeAttribute("hidden");
    panelRoot.setAttribute("aria-hidden", "false");
    ui.classList.add("home-sheet-panel-open");
    document.body.classList.add("home-sheet-panel-open");
    requestAnimationFrame(() => {
      panelRoot
        .querySelector<HTMLButtonElement>(".home-sheet-upload-cta")
        ?.focus({ preventScroll: true });
    });
  };

  const closeModal = () => {
    document.body.classList.remove("home-modal-open");
    ui.classList.remove("home-modal-visible");
    const rootEl = ui.querySelector<HTMLElement>("#home-modal-root");
    if (!rootEl || rootEl.hasAttribute("hidden")) return;
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && rootEl.contains(focused)) {
      focused.blur();
    }
    rootEl.setAttribute("hidden", "");
    rootEl.setAttribute("aria-hidden", "true");
    ui.querySelector<HTMLButtonElement>("#home-enter-edit")?.focus({ preventScroll: true });
  };

  const showModalPanel = (id: HomeModalId) => {
    closeShopModal();
    const rootEl = ui.querySelector<HTMLElement>("#home-modal-root");
    const title = ui.querySelector<HTMLElement>("#home-modal-title");
    if (!rootEl) return;
    rootEl.removeAttribute("hidden");
    rootEl.setAttribute("aria-hidden", "false");
    if (title) title.textContent = MODAL_TITLES[id];
    ui.querySelectorAll<HTMLElement>("[data-modal-panel]").forEach((panel) => {
      panel.hidden = panel.dataset.modalPanel !== id;
    });
    if (id === "single") {
      resetPartSingleForm();
      if (shopTab === "floor") {
        partKindPicker?.setValue("floor");
        const wInput = ui.querySelector<HTMLInputElement>("#part-w");
        const hInput = ui.querySelector<HTMLInputElement>("#part-h");
        if (wInput) wInput.value = "1";
        if (hInput) hInput.value = "1";
      }
      if (shopTab === "shell") {
        partKindPicker?.setValue("shell");
      }
    }
    document.body.classList.add("home-modal-open");
    ui.classList.add("home-modal-visible");
    requestAnimationFrame(() => {
      rootEl
        .querySelector<HTMLButtonElement>(".home-modal-close")
        ?.focus({ preventScroll: true });
    });
  };

  let setEditMode: (enabled: boolean) => void = () => {};

  const openShopModal = () => {
    if (!perms.canManageCatalog) return;
    if (!editMode) setEditMode(true);
    closeModal();
    closeCurrencyShopModal();
    const shopRoot = ui.querySelector<HTMLElement>("#home-shop-modal");
    if (!shopRoot) return;
    shopRoot.removeAttribute("hidden");
    shopRoot.setAttribute("aria-hidden", "false");
    ui.classList.add("home-shop-open");
    renderPalette();
    syncPlaceSizeControls();
    syncBulkDeleteControls();
    requestAnimationFrame(() => {
      shopRoot
        .querySelector<HTMLButtonElement>(".home-shop-close")
        ?.focus({ preventScroll: true });
    });
  };

  const placePartFromShop = (partId: string): boolean => {
    const part = homeData.parts.find((p) => p.id === partId);
    if (!part) return false;
    const hint = ui.querySelector<HTMLElement>("#home-mode-hint");
    const kind = normalizePartKind(part.kind);

    if (kind === "backdrop") {
      tryPlacePart(partId, 0, 0);
      selectedPartId = partId;
      armedPartId = null;
      selectedPlacementId = null;
      getHomeScene()?.clearSelectedPlacement();
      getHomeScene()?.setShowSockets(false);
      if (hint) hint.textContent = "ตั้งเป็นฉากหลังทั้งห้องแล้ว";
      return true;
    }

    if (kind === "shell") {
      tryPlacePart(partId, 0, 0);
      selectedPartId = partId;
      armedPartId = null;
      selectedPlacementId = null;
      getHomeScene()?.clearSelectedPlacement();
      getHomeScene()?.setShowSockets(false);
      if (hint) hint.textContent = "ตั้งเป็นฉากรอบกรอบนอกกริดแล้ว";
      return true;
    }

    const cell = findFirstPlaceCell(homeData, partId);
    if (!cell) {
      if (hint) {
        hint.textContent =
          kindQuotaRemaining(homeData, kind) <= 0
            ? `โควต้า${slotLabelTh(kind)}เต็ม · เอาชิ้นเก่าออกก่อน`
            : "วางไม่ได้ · กริดเต็มหรือไม่มีช่องว่าง";
      }
      return false;
    }

    const snapped = resolvePlaceCell(homeData, partId, cell.col, cell.row);
    if (!tryPlacePart(partId, snapped.col, snapped.row)) {
      if (hint) hint.textContent = "วางไม่ได้ที่นี่ · ชนชิ้นอื่น เกินกริด หรือเต็มโควต้า";
      return false;
    }

    selectedPartId = partId;
    armedPartId = null;
    selectedPlacementId = null;
    getHomeScene()?.clearSelectedPlacement();
    getHomeScene()?.setShowSockets(false);
    if (hint) {
      hint.textContent = snapped.snapped
        ? `วาง ${part.name} แล้ว · snap เข้าจุดวาง`
        : `วาง ${part.name} แล้ว`;
    }
    return true;
  };

  setEditMode = (enabled: boolean) => {
    if (enabled && !perms.canEditHomeLayout) return;
    editMode = enabled;
    if (enabled) {
      setSitRestEnabled(false);
      if (!editSessionSnapshot) beginEditSession();
    } else {
      armedPartId = null;
      selectedPlacementId = null;
      getHomeScene()?.clearSelectedPlacement();
      closeModal();
      closeShopModal();
      closeSheetPanel();
      editSessionSnapshot = null;
    }
    window.dispatchEvent(
      new CustomEvent(HomeEvents.EditModeChanged, { detail: { enabled } }),
    );
    const editChrome = ui.querySelector<HTMLElement>("#home-edit-chrome");
    const enterEdit = ui.querySelector<HTMLButtonElement>("#home-enter-edit");
    ui.classList.toggle("home-editing", enabled);
    if (editChrome) editChrome.hidden = !enabled;
    if (enterEdit) {
      enterEdit.setAttribute("aria-pressed", enabled ? "true" : "false");
      enterEdit.classList.toggle("home-rail-btn--active", enabled);
    }
    const badge = ui.querySelector<HTMLElement>("#home-furniture-badge");
    if (badge) badge.hidden = !enabled;
    if (!enabled) {
      draggingPartId = null;
      getHomeScene()?.clearPlaceGhost();
      getHomeScene()?.setShowSockets(false);
    }
    renderPalette();
    syncPlaceSizeControls();
    updateModeHint();
    renderEditLayerPanel();
    structureMount?.syncStructureChrome();
  };

  const avatarSrc = options.avatarSrc;
  const showHomeSettings = options.shell === "home";
  const hudRankTitle =
    features.progressionHud && rankCosmetics.weekly?.grants?.length
      ? weeklyRankBuffHint(rankCosmetics.weekly.grants)
      : "";
  const gmConsoleInSettings =
    showHomeSettings && resolveGmAccess(user).canAccessGmConsole;
  const accountLabel = user.displayName ?? user.email ?? "ผู้เล่น";
  const leftRailExtra =
    options.shell === "tower"
      ? ""
      : options.shell === "creator"
        ? `
      <a class="home-rail-btn" href="/creator" aria-label="รายการฉาก">
        ${homeIcon("place")}
        <span class="home-rail-label">ฉาก</span>
      </a>
      <a class="home-rail-btn" href="/home" aria-label="บ้านของฉัน">
        ${homeIcon("character")}
        <span class="home-rail-label">บ้าน</span>
      </a>`
        : "";

  const railMenuFeatures: HomeRailMenuFeatures = {
    skillsModals: features.skillsModals,
    cashShop: features.cashShop,
    walletHud: features.walletHud,
  };
  const homeRailFrameMarkup =
    options.shell === "home" ? renderHomeRailFrameMarkup(railMenuFeatures) : "";

  bootLoader.setProgress(82, "สร้างหน้าจอ…");

  ui.innerHTML = `
    <div id="game-shell" class="home-shell home-shell--immersive">
      <div class="home-stage-wrap">
        <div id="game-container" class="home-game-container"></div>
      </div>
    </div>

    <header class="home-hud-top" aria-label="สถานะ">
      <div class="home-hud-bar home-hud-bar--primary"${hudRankTitle ? ` title="${escapeHtml(hudRankTitle)}"` : ""}>
        <div class="home-hud-bar__identity">
          <div class="home-hud-fighter-card">
            <div class="home-hud-avatar-wrap">
              <img class="home-hud-avatar" src="${escapeHtml(avatarSrc)}" alt="" width="52" height="52" title="${escapeHtml(options.profileName)}" />
            </div>
            <div class="home-hud-fighter-main">
              ${
                features.progressionHud
                  ? `<div class="home-hud-fighter-badges">
                <span class="home-hud-sub" id="home-hud-sub">Level ${progression.level}</span>
                <span class="home-combat-power-pill" id="home-combat-power-pill" title="ค่าพลังรวม">
                  <span class="home-combat-power-mark" aria-hidden="true">⚔</span>
                  <span id="home-combat-power-value">—</span>
                </span>
                <span class="home-combat-hit-best-pill" id="home-combat-hit-best-pill" title="Hit สูงสุดจากสนามรบ (หอ/โจมตี/ด่าน) · ไม่นับการตีในบ้าน">
                  <span class="home-combat-hit-best-mark" aria-hidden="true">⚡</span>
                  Hit <span id="home-combat-hit-best-value">0</span>
                </span>
              </div>
              <div class="home-hud-vitals home-combat-hp-pill" id="home-combat-hp-pill" title="HP และมานา · ${escapeHtml(options.profileName)}">
                <div class="home-combat-vital-row home-combat-vital-row--hp">
                  <span class="home-combat-hp-bar home-combat-vital-bar" aria-hidden="true">
                    <span class="home-combat-hp-bar-fill" id="home-combat-hp-fill"></span>
                    <span class="home-combat-vital-value" id="home-combat-hp-value">—</span>
                  </span>
                  <span class="home-combat-hp-delta" id="home-combat-hp-delta" hidden aria-live="polite"></span>
                </div>
                <div class="home-combat-vital-row home-combat-vital-row--mp home-combat-mana-pill" id="home-combat-mana-pill">
                  <span class="home-combat-mana-bar home-combat-vital-bar" aria-hidden="true">
                    <span class="home-combat-mana-bar-fill" id="home-combat-mana-fill"></span>
                    <span class="home-combat-vital-value" id="home-combat-mana-value">—</span>
                  </span>
                </div>
              </div>`
                  : ""
              }
            </div>
          </div>
        </div>
      </div>
      <div class="home-hud-bar home-hud-bar--secondary" aria-label="ทรัพยากรและเครื่องมือ">
        <div class="home-hud-resources home-hud-money" aria-label="เงิน"${features.walletHud ? "" : " hidden"}>
          <span class="home-resource-pill home-resource-pill--wallet" title="ทอง">
            <img src="${HOME_ASSETS.coin}" alt="" width="18" height="18" />
            <span id="home-wallet-coins">${economy.wallet.coins.toLocaleString("en-US")}</span>
          </span>
          <span class="home-resource-pill home-resource-pill--wallet" title="เพชร">
            <img src="${HOME_ASSETS.gem}" alt="" width="18" height="18" />
            <span id="home-wallet-gems">${economy.wallet.gems.toLocaleString("en-US")}</span>
          </span>
          <span class="home-resource-pill home-resource-pill--cash home-resource-pill--hud-extra" title="Cash">
            <span class="home-cash-mark" aria-hidden="true">$</span>
            <span id="home-wallet-cash">${economy.wallet.cash.toLocaleString("en-US")}</span>
          </span>
        </div>
        <div class="home-hud-bar__tools" aria-label="เลือกโหมด">
          <div id="home-game-mode-host" class="home-game-mode-host game-mode-host--compact game-mode-host--hud-wide"${features.gameModeControl ? "" : " hidden"}></div>
        </div>
        <div class="home-hud-trailing" aria-label="เวลาและตั้งค่า">
          <time id="home-utc-clock" class="home-utc-clock home-utc-clock--hud-compact" datetime="" title="เวลา UTC">${formatUtcClockCompact()}</time>
          <button type="button" id="home-theme-toggle" class="home-theme-toggle home-theme-toggle--icon-only" aria-label="เปลี่ยนโทนแสง" title="กดสลับกลางวัน/กลางคืน · ค่าเริ่มตามเวลา server">
            <span class="home-theme-toggle-icon" aria-hidden="true">${homeIcon(themeById(homeData.themeId).id === "night" ? "moon" : "sun")}</span>
          </button>
          ${
            showHomeSettings
              ? `<button type="button" class="home-hud-settings home-theme-toggle home-theme-toggle--icon-only" data-open-home-settings aria-label="ตั้งค่า" title="ตั้งค่า">
            <span class="home-theme-toggle-icon" aria-hidden="true">${homeIcon("settings")}</span>
          </button>`
              : ""
          }
        </div>
      </div>
    </header>

    ${
      features.progressionHud
        ? `<div class="home-hud-focus" id="home-hud-focus" hidden aria-label="เป้าหมายที่โฟกัส">
      <img class="home-hud-focus-img" id="home-hud-focus-img" src="${HOME_ASSETS.shopTabs.monster}" alt="" width="36" height="36" />
      <div class="home-hud-focus-body">
        <span class="home-hud-focus-name" id="home-hud-focus-name"></span>
        <span class="home-hud-focus-hp" id="home-hud-focus-hp"></span>
        <span class="home-hud-focus-bar" aria-hidden="true">
          <span class="home-hud-focus-fill" id="home-hud-focus-fill"></span>
        </span>
        <span class="home-hud-focus-delta" id="home-hud-focus-delta" hidden aria-live="polite"></span>
      </div>
    </div>`
        : ""
    }

    <aside id="home-gm-tools-host" class="home-gm-tools-host" aria-hidden="true" aria-label="เครื่องมือ GM"></aside>

    <nav class="home-rail home-rail--left home-rail--compact" aria-label="เมนูหลัก">
      ${
        homeRailFrameMarkup ||
        `<div class="home-rail-primary">
        <button type="button" id="home-enter-edit" class="home-rail-btn home-rail-btn--tile" aria-pressed="false" aria-label="แต่งบ้าน">
          ${homeRailIcon("decorate")}
          <span class="home-rail-label">แต่ง</span>
        </button>
        ${leftRailExtra}
      </div>`
      }
    </nav>

    <aside id="home-edit-layer-panel" class="home-edit-layer-panel" hidden aria-label="ชั้นการวาง">
      <header class="home-edit-layer-panel-head">
        <button type="button" id="home-edit-layer-collapse" class="home-edit-layer-collapse" aria-expanded="true" aria-label="ซ้อนแผงชั้นเข้าขอบซ้าย" title="ซ้อนเข้าขอบซ้าย">‹</button>
        <span class="home-edit-layer-panel-title">ชั้น</span>
        <span class="home-edit-layer-panel-hint">หน้า → หลัง</span>
      </header>
      <div id="home-edit-layer-list" class="home-edit-layer-list"></div>
    </aside>

    <aside id="home-edit-chrome" class="home-edit-chrome" hidden aria-label="เครื่องมือแต่ง">
      <button type="button" id="home-rail-catalog" class="home-rail-btn home-rail-btn--tile home-edit-chrome-btn" aria-label="เปิดรายการชิ้น">
        ${homeRailTileIcon("sheet")}
        <span class="home-rail-label">รายการ</span>
      </button>
      <button type="button" id="home-rail-confirm" class="home-rail-btn home-rail-btn--tile home-rail-btn--confirm home-edit-chrome-btn" aria-label="บันทึกและออกจากการแต่ง">
        ${homeRailTileIcon("done")}
        <span class="home-rail-label">เสร็จ</span>
      </button>
      <button type="button" id="home-edit-discard" class="home-rail-btn home-rail-btn--tile home-edit-chrome-btn home-edit-chrome-btn--exit" aria-label="ออกโดยไม่บันทึก">
        ${homeRailTileIcon("close")}
        <span class="home-rail-label">ออก</span>
      </button>
    </aside>

    <p class="home-furniture-badge" id="home-furniture-badge" hidden>
      <span class="home-furniture-badge-segment">
        คลัง <strong id="home-furniture-count">0 / 0</strong>
      </span>
      <span id="home-kind-quota-badge" class="home-furniture-badge-segment home-kind-quota-badge" title="โควต้าวางในฉาก">
        โควต้า <strong id="home-kind-quota-count">0 / 240</strong>
      </span>
    </p>

    <div class="home-combat-hotbar"${features.skillsBar || features.healthPotion ? "" : " hidden"}>
      ${features.skillsBar ? playSkillBarRowMarkup("home-skill-bar", "home-skill-auto-all") : ""}
      ${features.healthPotion ? renderItemHotbarHtml("home") : ""}
    </div>

    <p class="hint home-room-hint home-float-hint" id="home-mode-hint" hidden></p>
    <p id="home-edit-feedback" class="home-edit-feedback" hidden aria-live="polite"></p>

    <div id="home-edit-selection" class="home-edit-selection home-edit-selection--game" hidden aria-label="ปรับชิ้นที่เลือก">
      <button
        type="button"
        id="home-open-parts-catalog"
        class="home-edit-action-tile home-edit-catalog-tile"
        hidden
        aria-label="เปิดคลังชิ้นส่วน"
        title="คลังชิ้น"
      >
        ${homeIcon("stash")}
      </button>
      <div id="home-place-size" class="home-place-size home-place-size--float home-edit-selection-body home-edit-selection-body--horizontal">
        <div class="home-place-kind-field home-place-kind-field--select">
          <div id="place-part-kind-host"></div>
        </div>
        <span class="home-edit-selection-split home-edit-selection-split--v" aria-hidden="true"></span>
        <div class="home-place-grid-field home-place-grid-field--game" role="group" aria-label="สเกลภาพในช่องกริด">
          <span class="home-edit-grid-badge" aria-hidden="true" title="สเกล">${homeIcon("grid")}</span>
          <button type="button" id="place-scale-minus" class="home-grid-step home-grid-step--game" aria-label="ลดสเกล" title="สเกล−">−</button>
          <input type="number" id="place-scale" class="text-input home-grid-num home-grid-num--game home-grid-num--scale" min="${HOME_PLACE_SCALE_MIN * 100}" max="${HOME_PLACE_SCALE_MAX * 100}" step="10" value="100" inputmode="numeric" aria-label="สเกลภาพเป็นเปอร์เซ็นต์" title="สเกล %" />
          <span class="home-place-scale-suffix" aria-hidden="true">%</span>
          <button type="button" id="place-scale-plus" class="home-grid-step home-grid-step--game" aria-label="เพิ่มสเกล" title="สเกล+">+</button>
        </div>
        <span class="home-edit-selection-split home-edit-selection-split--v" aria-hidden="true"></span>
        <div class="home-edit-grid-nudge" role="group" aria-label="เลื่อนตำแหน่งบนกริด">
          <button type="button" id="home-nudge-up" class="home-grid-step home-grid-step--game home-grid-nudge-btn" aria-label="เลื่อนขึ้น" title="ขึ้น">↑</button>
          <div class="home-edit-grid-nudge-row">
            <button type="button" id="home-nudge-left" class="home-grid-step home-grid-step--game home-grid-nudge-btn" aria-label="เลื่อนซ้าย" title="ซ้าย">←</button>
            <button type="button" id="home-nudge-right" class="home-grid-step home-grid-step--game home-grid-nudge-btn" aria-label="เลื่อนขวา" title="ขวา">→</button>
          </div>
          <button type="button" id="home-nudge-down" class="home-grid-step home-grid-step--game home-grid-nudge-btn" aria-label="เลื่อนลง" title="ลง">↓</button>
        </div>
        <label id="place-floor-room-wrap" class="home-place-floor-check home-edit-floor-tile" hidden title="พื้นทั้งห้อง">
          <input type="checkbox" id="place-as-room-floor" />
          <img class="home-edit-floor-tile-icon" src="${HOME_ASSETS.shopTabs.floor}" alt="" width="22" height="22" decoding="async" />
          <span class="visually-hidden">พื้นทั้งห้อง</span>
        </label>
        <span class="home-edit-selection-split home-edit-selection-split--v" aria-hidden="true"></span>
        <div class="home-edit-selection-actions home-edit-selection-actions--row" role="group" aria-label="การดำเนินการชิ้นที่เลือก">
          <button type="button" id="home-placement-copy" class="home-edit-selection-copy home-edit-action-tile" disabled aria-label="คัดลอกชิ้นที่วาง" title="คัดลอกชิ้นที่วาง">
            ${homeIcon("copy")}
          </button>
          <button type="button" id="home-rail-delete" class="home-edit-action-tile home-edit-action-tile--danger" disabled aria-label="ลบชิ้นที่เลือก" title="ลบ">
            ${homeIcon("trash")}
          </button>
          <button type="button" id="home-edit-unfocus" class="home-edit-action-tile" aria-label="ยกเลิกเลือก" title="ออกจากการเลือก">
            ${homeIcon("close")}
          </button>
        </div>
      </div>
    </div>

    <div id="home-shop-modal" class="home-shop-modal" hidden aria-hidden="true">
      <button type="button" class="home-shop-backdrop" data-close-shop aria-label="ปิดรายการ"></button>
      <div class="home-shop-dialog" role="dialog" aria-modal="true" aria-labelledby="home-shop-title">
        <header class="home-shop-head">
          <h2 id="home-shop-title" class="visually-hidden">เลือกลงฉาก</h2>
          <div class="home-shop-tabs" role="tablist" id="home-shop-tabs">
            ${HOME_SHOP_TABS.map((tab) => {
              const icon = HOME_ASSETS.shopTabs[tab.id];
              return `
              <button type="button" class="home-shop-tab${tab.id === "all" ? " home-shop-tab--active" : ""}" role="tab" data-shop-tab="${tab.id}" aria-label="${tab.labelTh}" title="${tab.labelTh}" aria-selected="${tab.id === "all" ? "true" : "false"}">
                <img class="home-shop-tab-icon" src="${icon}" alt="" width="20" height="20" />
              </button>`;
            }).join("")}
          </div>
          <div class="home-shop-tools">
            <button type="button" id="home-shop-bulk-toggle" class="home-shop-tool home-shop-tool--bulk" aria-pressed="false" aria-label="เลือกหลายรายการ" title="เลือกหลายรายการ">
              ${homeIcon("checklist")}
            </button>
            <button type="button" class="home-shop-tool" data-open-sheet-panel aria-label="อัปโหลดรูป" title="เพิ่ม">
              ${homeIcon("add")}
            </button>
            <button type="button" class="btn btn-ghost btn-icon home-shop-close" data-close-shop aria-label="ปิด">
              ${homeIcon("close")}
            </button>
          </div>
        </header>
        <div class="home-theme-row">
          <div class="home-grid-size" role="group" aria-label="ขนาดกริด" title="ขนาดกริด ${HOME_GRID_COLS_MIN}–${HOME_GRID_COLS_MAX} × ${HOME_GRID_ROWS_MIN}–${HOME_GRID_ROWS_MAX}">
            <span class="home-compact-icon" aria-hidden="true">${homeIcon("grid")}</span>
            <input type="number" id="home-grid-cols" class="text-input home-grid-num" min="${HOME_GRID_COLS_MIN}" max="${HOME_GRID_COLS_MAX}" value="${homeData.gridCols}" inputmode="numeric" aria-label="ความกว้างกริด" title="กว้าง" />
            <button type="button" id="home-grid-cols-plus" class="home-grid-step" aria-label="เพิ่มความกว้าง" title="กว้าง+">+</button>
            <span class="home-place-size-x" aria-hidden="true">×</span>
            <input type="number" id="home-grid-rows" class="text-input home-grid-num" min="${HOME_GRID_ROWS_MIN}" max="${HOME_GRID_ROWS_MAX}" value="${homeData.gridRows}" inputmode="numeric" aria-label="ความสูงกริด" title="สูง" />
            <button type="button" id="home-grid-rows-plus" class="home-grid-step" aria-label="เพิ่มความสูง" title="สูง+">+</button>
            <button type="button" id="home-grid-apply" class="home-grid-apply" aria-label="ใช้ขนาดกริด" title="ใช้ขนาด">
              ${homeIcon("check")}
            </button>
          </div>
          <div class="home-slot-bar" aria-label="ช่องคลัง">
            ${homeAutoRemoveBgToggleMarkup({
              id: "home-shop-auto-remove-bg",
              extraClass: "home-shop-bg-auto",
              compact: true,
            })}
            <span id="home-slot-label" class="home-slot-label">—</span>
            <button type="button" id="home-slot-open-store" class="home-slot-buy" title="ซื้อช่องเพชรที่ร้าน">
              ${homeIcon("shop")}<span>ร้าน</span>
            </button>
          </div>
        </div>
        <div id="home-palette" class="home-palette home-palette--shop"></div>
        <footer class="home-shop-footer">
          <div id="home-shop-bulk-bar" class="home-shop-bulk-bar" hidden>
            <button type="button" id="home-shop-select-all" class="btn btn-ghost home-shop-select-all" disabled>
              เลือกทั้งหมด
            </button>
            <button type="button" id="home-shop-delete-selected" class="btn btn-ghost home-shop-delete-selected" disabled>
              ลบที่เลือก
            </button>
          </div>
          <span class="home-shop-footer-spacer"></span>
          <button type="button" class="btn btn-ghost" data-close-shop>ปิด</button>
        </footer>
      </div>
    </div>

    ${features.characterSheet ? characterSheetModalMarkup() : ""}
    ${features.skillsModals ? skillsModalMarkup() : ""}

    ${features.cashShop ? currencyShopModalMarkup(economy, currencyShopTab, shopPageByTab, dungeonMonsterShopContext()) : ""}
    ${features.walletHud ? stashModalMarkup() : ""}

    ${
      showHomeSettings
        ? `
    <div id="home-settings-modal" class="home-settings-modal" hidden aria-hidden="true">
      <button type="button" class="home-settings-backdrop" data-close-home-settings aria-label="ปิด"></button>
      <div class="home-settings-dialog" role="dialog" aria-modal="true" aria-labelledby="home-settings-title">
        <header class="home-settings-head">
          <h2 id="home-settings-title" class="home-settings-title">ตั้งค่า</h2>
          <button type="button" class="btn btn-ghost btn-icon" data-close-home-settings aria-label="ปิด">
            ${homeIcon("close")}
          </button>
        </header>
        <div class="home-settings-body">
          <p class="home-settings-account hint">${escapeHtml(accountLabel)}</p>
          ${
            gmConsoleInSettings
              ? `<a class="btn btn-ghost home-settings-link" href="/gm">GM Console</a>`
              : ""
          }
          <button type="button" class="btn btn-ghost home-settings-sign-out" id="home-settings-sign-out">
            ออกจากระบบ
          </button>
        </div>
      </div>
    </div>`
        : ""
    }

    <div id="home-delete-modal" class="home-delete-modal" hidden aria-hidden="true">
      <button type="button" class="home-delete-backdrop" data-close-delete aria-label="ปิด"></button>
      <div class="home-delete-dialog" role="dialog" aria-modal="true" aria-labelledby="home-delete-title">
        <header class="home-delete-head">
          <h2 id="home-delete-title" class="home-delete-title">ลบจากคลัง</h2>
          <button type="button" class="btn btn-ghost btn-icon" data-close-delete aria-label="ปิด">
            ${homeIcon("close")}
          </button>
        </header>
        <p class="hint home-delete-lede">ตรวจรายการก่อนยืนยัน · ชิ้นที่ยังอยู่ในฉากต้องเอาออกจากฉากก่อน</p>
        <p id="home-delete-blocked-hint" class="hint home-delete-blocked-hint" hidden></p>
        <ul id="home-delete-list" class="home-delete-list"></ul>
        <footer class="home-delete-footer">
          <button type="button" class="btn btn-ghost" data-close-delete>ยกเลิก</button>
          <button type="button" id="home-delete-confirm" class="btn btn-primary home-delete-confirm" disabled>
            ยืนยันลบ
          </button>
        </footer>
      </div>
    </div>

    <div id="home-sheet-panel" class="home-sheet-panel" hidden aria-hidden="true">
      <button type="button" class="home-sheet-backdrop" data-close-sheet-panel aria-label="ปิด"></button>
      <div class="home-sheet-panel-shell" role="dialog" aria-modal="true" aria-labelledby="home-sheet-panel-title">
        <header class="home-sheet-panel-head">
          <button type="button" class="btn btn-ghost btn-icon home-sheet-panel-close" data-close-sheet-panel aria-label="ปิด">
            ${homeIcon("close")}
          </button>
          <div class="home-sheet-panel-head-text">
            <p class="home-sheet-panel-kicker">คลังเฟอร์นิเจอร์</p>
            <h2 id="home-sheet-panel-title" class="home-sheet-panel-title">เลือกรูป</h2>
            <p id="home-sheet-panel-lede" class="hint home-sheet-panel-lede" hidden></p>
          </div>
          <span class="home-sheet-panel-head-spacer" aria-hidden="true"></span>
        </header>
        <div id="home-sheet-crop-host" class="home-sheet-crop-host"></div>
      </div>
    </div>

    <div id="home-modal-root" class="home-modal-root" hidden aria-hidden="true">
      <div class="home-modal" role="dialog" aria-modal="true" aria-labelledby="home-modal-title">
        <header class="home-modal-head">
          <h2 id="home-modal-title" class="home-modal-title">อัปโหลด</h2>
          <button type="button" class="btn btn-ghost btn-icon home-modal-close" data-close-modal aria-label="ปิด">
            ${homeIcon("close")}
          </button>
        </header>
        <div class="home-modal-body">
          <section class="home-modal-panel" data-modal-panel="single">
            <div id="part-single-upload-step" class="home-part-upload-step">
              <label class="btn btn-primary sheet-upload btn-block home-part-upload-cta">
                ${homeIcon("image")} เลือกรูปจากเครื่อง
                <input type="file" id="part-image" accept="image/png,image/jpeg,image/webp" hidden />
              </label>
              <p class="hint home-part-upload-lede">เลือกรูปก่อน · แล้วเลือกชิ้นเดียวหรือหลายชิ้น</p>
              ${homeAutoRemoveBgToggleMarkup({
                id: "part-auto-remove-bg",
                extraClass: "home-part-bg-auto",
              })}
              <p id="part-form-error" class="form-error" role="alert" hidden></p>
            </div>
            <div id="part-single-after-upload" class="home-part-after-upload" hidden>
              <div class="home-part-upload-preview-wrap">
                <img id="part-single-preview" class="home-part-upload-preview" alt="" width="96" height="96" />
                <button type="button" id="part-change-image" class="btn btn-ghost btn-sm">
                  เปลี่ยนรูป
                </button>
              </div>
              <div id="part-single-mode-step" class="home-part-mode-step">
                <p class="hint home-part-mode-lede">รูปนี้เป็นชิ้นเดียว หรือแผ่นที่มีหลายชิ้น?</p>
                <div class="home-part-mode-choices">
                  <button type="button" id="part-mode-single" class="btn btn-primary home-part-mode-btn">
                    ชิ้นเดียว
                  </button>
                  <button type="button" id="part-mode-multi" class="btn btn-ghost home-part-mode-btn">
                    หลายชิ้น
                  </button>
                </div>
                <p class="hint">หลายชิ้น → ครอปกริด (คอลัมน์×แถว) / แยกอัตโนมัติ</p>
              </div>
              <div id="part-single-config-step" class="home-part-config-step" hidden>
              <label class="field">
                <span class="field-label">ชื่อ</span>
                <input type="text" id="part-name" class="text-input" maxlength="24" placeholder="เช่น โต๊ะ" />
              </label>
              <div class="field home-part-kind-field">
                <span class="field-label">หมวด</span>
                <div id="part-kind-host"></div>
              </div>
              <div class="home-part-size-row">
                <label class="field field--inline">
                  <span class="field-label">กว้าง</span>
                  <input type="number" id="part-w" class="text-input" min="1" max="4" value="1" inputmode="numeric" />
                </label>
                <label class="field field--inline">
                  <span class="field-label">สูง</span>
                  <input type="number" id="part-h" class="text-input" min="1" max="4" value="1" inputmode="numeric" />
                </label>
              </div>
              <button type="button" id="part-add" class="btn btn-primary btn-block">เพิ่มชิ้นส่วน</button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  `;

  document.body.classList.remove("home-modal-open");
  closeModal();

  const container = ui.querySelector<HTMLElement>("#game-container");
  if (!container) {
    throw new Error("Missing #game-container");
  }

  let pendingPartImage: string | null = null;
  let pendingPartOriginal: string | null = null;
  let pendingPartFileName = "";
  /** null = ยังไม่เลือกโหมดหลังอัปโหลด */
  let partSingleMode: "single" | null = null;
  let sheetCropHandle: HomeSheetCropHandle | undefined;

  const showFormError = (message: string) => {
    const el = ui.querySelector<HTMLElement>("#part-form-error");
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  };

  const hideFormError = () => {
    const el = ui.querySelector<HTMLElement>("#part-form-error");
    if (!el) return;
    el.textContent = "";
    el.hidden = true;
  };

  const syncPartSingleSteps = () => {
    const hasImage = Boolean(pendingPartImage);
    const uploadStep = ui.querySelector<HTMLElement>("#part-single-upload-step");
    const afterUpload = ui.querySelector<HTMLElement>("#part-single-after-upload");
    const modeStep = ui.querySelector<HTMLElement>("#part-single-mode-step");
    const configStep = ui.querySelector<HTMLElement>("#part-single-config-step");
    if (uploadStep) uploadStep.hidden = hasImage;
    if (afterUpload) afterUpload.hidden = !hasImage;
    if (modeStep) modeStep.hidden = !hasImage || partSingleMode !== null;
    if (configStep) configStep.hidden = !hasImage || partSingleMode !== "single";
    const preview = ui.querySelector<HTMLImageElement>("#part-single-preview");
    if (preview) {
      if (hasImage && pendingPartImage) {
        preview.src = pendingPartImage;
        preview.hidden = false;
      } else {
        preview.removeAttribute("src");
        preview.hidden = true;
      }
    }
  };

  const resetPartSingleForm = () => {
    pendingPartImage = null;
    pendingPartOriginal = null;
    pendingPartFileName = "";
    partSingleMode = null;
    const nameInput = ui.querySelector<HTMLInputElement>("#part-name");
    const wInput = ui.querySelector<HTMLInputElement>("#part-w");
    const hInput = ui.querySelector<HTMLInputElement>("#part-h");
    const fileInput = ui.querySelector<HTMLInputElement>("#part-image");
    if (nameInput) nameInput.value = "";
    if (wInput) wInput.value = "1";
    if (hInput) hInput.value = "1";
    partKindPicker?.setValue("solid");
    if (fileInput) fileInput.value = "";
    hideFormError();
    syncPartSingleSteps();
  };

  const suggestCellsFromImage = (width: number, height: number): { cellsW: number; cellsH: number } => ({
    cellsW: clampPartCells(Math.ceil(width / HOME_CELL_PX)),
    cellsH: clampPartCells(Math.ceil(height / HOME_CELL_PX)),
  });

  const defaultPartNameFromFile = (fileName: string): string => {
    const base = fileName.replace(/\.[^.]+$/, "").trim();
    return base.slice(0, 24);
  };

  const loadImageNaturalSize = (dataUrl: string): Promise<{ width: number; height: number }> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error("อ่านขนาดรูปไม่ได้"));
      img.src = dataUrl;
    });

  const renderPalette = () => {
    const palette = ui.querySelector<HTMLElement>("#home-palette");
    if (!palette) return;

    ui.querySelectorAll<HTMLButtonElement>("[data-shop-tab]").forEach((btn) => {
      const id = (btn.dataset.shopTab ?? "all") as HomeShopTab;
      const active = id === shopTab;
      btn.classList.toggle("home-shop-tab--active", active);
      btn.setAttribute("aria-selected", active ? "true" : "false");
      if (id === "all") {
        const used = totalCatalogUsed(homeData);
        const cap = totalCatalogCap(economy);
        btn.title = `ทั้งหมด ${used}/${cap}`;
        btn.setAttribute("aria-label", `ทั้งหมด ${used}/${cap}`);
      } else {
        const used = catalogSlotUsed(homeData, id);
        const cap = catalogSlotCap(economy, id);
        const name = slotLabelTh(id);
        btn.title = `${name} ${used}/${cap}`;
        btn.setAttribute("aria-label", `${name} ${used}/${cap}`);
        btn.classList.toggle("home-shop-tab--full", used >= cap);
      }
    });
    syncSlotBar();
    syncBulkDeleteControls();

    const visibleParts = homeData.parts.filter((part) => {
      if (shopTab === "all") return true;
      return normalizePartKind(part.kind) === shopTab;
    });

    if (homeData.parts.length === 0) {
      palette.innerHTML = `
        <div class="home-palette-empty-state">
          <p class="hint home-palette-empty">ยังไม่มีชิ้น</p>
          <p class="hint home-palette-empty-lede">อัปโหลดรูปหรือเพิ่มชิ้นเดียวเพื่อเริ่มแต่งบ้าน</p>
          <div class="home-palette-empty-actions">
            <button type="button" class="btn btn-primary btn-sm" data-open-sheet-panel>อัปโหลด</button>
            <button type="button" class="btn btn-ghost btn-sm" data-open-modal="single">ชิ้นเดียว</button>
          </div>
        </div>`;
      palette.querySelectorAll<HTMLButtonElement>("[data-open-sheet-panel]").forEach((btn) => {
        btn.addEventListener("click", () => openSheetPanel());
      });
      palette.querySelectorAll<HTMLButtonElement>("[data-open-modal]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const id = btn.dataset.openModal as HomeModalId | undefined;
          if (id) showModalPanel(id);
        });
      });
      return;
    }

    if (visibleParts.length === 0) {
      const emptyKind = shopTab === "all" ? "solid" : shopTab;
      palette.innerHTML = `
        <div class="home-palette-empty-state">
          <p class="hint home-palette-empty">แท็บนี้ยังไม่มีชิ้น</p>
          <p class="hint home-palette-empty-lede">ช่อง ${catalogSlotUsed(homeData, emptyKind)}/${catalogSlotCap(economy, emptyKind)} · กด + ด้านบนเพื่อเพิ่ม</p>
          <div class="home-palette-empty-actions">
            <button type="button" class="btn btn-primary btn-sm" data-open-sheet-panel>อัปโหลด</button>
          </div>
        </div>`;
      palette.querySelectorAll<HTMLButtonElement>("[data-open-sheet-panel]").forEach((btn) => {
        btn.addEventListener("click", () => openSheetPanel());
      });
      return;
    }

    palette.replaceChildren();
    for (const part of visibleParts) {
      const kind = normalizePartKind(part.kind);
      const selected = part.id === selectedPartId ? " home-part-chip--selected" : "";
      const armed = part.id === armedPartId ? " home-part-chip--armed" : "";
      const bulkOn = catalogBulkSelected.has(part.id);
      const usage = catalogPartUsage(part.id);
      const qUsed = kindQuotaUsed(homeData, kind);
      const qCap = kindQuotaCap(kind);
      const card = document.createElement("div");
      card.className = `home-part-card${bulkOn ? " home-part-card--bulk" : ""}`;

      if (shopBulkMode) {
        const pick = document.createElement("label");
        pick.className = "home-part-pick";
        pick.title = usage.inUse ? usage.reasons.join(" · ") : "เลือกเพื่อลบ";
        const pickInput = document.createElement("input");
        pickInput.type = "checkbox";
        pickInput.className = "home-part-pick-input";
        pickInput.checked = bulkOn;
        pickInput.setAttribute("aria-label", `เลือก ${part.name}`);
        const pickMark = document.createElement("span");
        pickMark.className = "home-part-pick-mark";
        pickMark.setAttribute("aria-hidden", "true");
        pickMark.innerHTML = homeIcon("check");
        const syncPickUi = () => {
          pick.classList.toggle("home-part-pick--on", pickInput.checked);
        };
        pickInput.addEventListener("click", (event) => event.stopPropagation());
        pickInput.addEventListener("change", () => {
          if (pickInput.checked) catalogBulkSelected.add(part.id);
          else catalogBulkSelected.delete(part.id);
          syncPickUi();
          syncBulkDeleteControls();
          renderPalette();
        });
        pick.append(pickInput, pickMark);
        syncPickUi();
        card.append(pick);
      }

      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = `home-part-chip${selected}${armed}`;
      chip.draggable = paletteChipHtmlDrag && kind !== "backdrop" && kind !== "shell";
      chip.dataset.partId = part.id;
      const badges = kindBadgeLabels(kind).join(" · ");
      chip.title = `${part.name} · ${part.cellsW}×${part.cellsH} · ${badges} · โควต้า${slotLabelTh(kind)} ${qUsed}/${qCap}`;

      const img = document.createElement("img");
      img.alt = "";
      img.src = imageDataUrlForDisplay(part.imageDataUrl);

      const textWrap = document.createElement("span");
      textWrap.className = "home-part-chip-text";
      const nameSpan = document.createElement("span");
      nameSpan.className = "home-part-chip-name";
      nameSpan.textContent = part.name;
      const sizeSpan = document.createElement("span");
      sizeSpan.className = "home-part-chip-size";
      sizeSpan.textContent = `${part.cellsW}×${part.cellsH}`;
      const badgeSpan = document.createElement("span");
      badgeSpan.className = "home-part-chip-badge";
      badgeSpan.textContent = kindPreset(kind).tabLabelTh;
      textWrap.append(nameSpan, sizeSpan, badgeSpan);
      chip.append(img, textWrap);

      const isShellPart = kind === "shell";
      if (isShellPart) {
        chip.classList.toggle(
          "home-part-chip--skin-active",
          homeData.shellPartId === part.id,
        );
      }
      if (kind === "solid") {
        const placed = homeData.placements.some((p) => p.partId === part.id);
        chip.classList.toggle("home-part-chip--skin-active", placed);
        if (
          !placed &&
          kindQuotaRemaining(homeData, kind) <= 0
        ) {
          chip.classList.add("home-part-chip--quota-full");
          chip.title += " · ลบบ้านเดิมในฉากก่อนวางชิ้นนี้";
        }
      }

      card.append(chip);

      if (isShellPart && !shopBulkMode) {
        const shellActive = homeData.shellPartId === part.id;
        const applyBtn = document.createElement("button");
        applyBtn.type = "button";
        applyBtn.className = "btn btn-primary btn-xs home-part-apply-skin";
        applyBtn.dataset.applyShellPart = part.id;
        applyBtn.textContent = shellActive ? "ใช้งานอยู่" : "ใช้งาน";
        applyBtn.setAttribute("aria-label", shellActive ? `${part.name} ใช้เป็นฉากอยู่` : `ใช้ ${part.name} เป็นฉากรอบกรอบ`);
        applyBtn.disabled = shellActive;
        card.append(applyBtn);
      }

      palette.append(card);
    }

    palette.querySelectorAll<HTMLButtonElement>("[data-apply-shell-part]").forEach((btn) => {
      const partId = btn.dataset.applyShellPart;
      if (!partId) return;
      btn.addEventListener("click", (event) => {
        event.stopPropagation();
        if (!placePartFromShop(partId)) return;
        renderPalette();
        syncPlaceSizeControls();
        syncDeleteDockState();
        updateModeHint();
      });
    });

    palette.querySelectorAll<HTMLButtonElement>(".home-part-chip").forEach((chip) => {
      const partId = chip.dataset.partId;
      if (!partId) return;
      const part = homeData.parts.find((p) => p.id === partId);
      const chipKind = part ? normalizePartKind(part.kind) : "solid";

      chip.addEventListener("click", () => {
        if (shopBulkMode) {
          if (catalogBulkSelected.has(partId)) catalogBulkSelected.delete(partId);
          else catalogBulkSelected.add(partId);
          syncBulkDeleteControls();
          renderPalette();
          return;
        }
        if (chipKind === "shell") {
          selectedPartId = partId;
          armedPartId = null;
          getHomeScene()?.clearPlaceGhost();
          renderPalette();
          syncPlaceSizeControls();
          syncDeleteDockState();
          updateModeHint();
          return;
        }
        if (
          chipKind === "solid" &&
          kindQuotaRemaining(homeData, chipKind) <= 0 &&
          !homeData.placements.some((p) => p.partId === partId)
        ) {
          const hint = ui.querySelector<HTMLElement>("#home-mode-hint");
          if (hint) {
            hint.textContent = `โควต้า${slotLabelTh(chipKind)}เต็ม · เอาชิ้นเก่าออกก่อน`;
          }
          return;
        }
        if (!placePartFromShop(partId)) return;
        renderPalette();
        syncPlaceSizeControls();
        syncDeleteDockState();
        updateModeHint();
      });

      chip.addEventListener("dragstart", (event) => {
        draggingPartId = partId;
        armedPartId = partId;
        selectedPartId = partId;
        event.dataTransfer?.setData(HOME_PART_DRAG_MIME, partId);
        event.dataTransfer?.setData("text/plain", partId);
        if (event.dataTransfer) event.dataTransfer.effectAllowed = "copy";
        closeModal();
        syncPlaceSizeControls();
        updateModeHint();
      });

      chip.addEventListener("dragend", () => {
        draggingPartId = null;
        getHomeScene()?.clearPlaceGhost();
      });
    });

    syncDeleteDockState();
    syncPlaceSizeControls();
    syncEditQuotaBadge();
  };

  const getHomeScene = (): HomeRoomScene | null => {
    if (!game) return null;
    const scene = game.scene.getScene(SceneKeys.HomeRoom);
    return scene instanceof HomeRoomScene ? scene : null;
  };

  const OVERLAY_BLOCK_SELECTORS = [
    "#home-skills-modal",
    "#home-character-modal",
    "#home-shop-modal",
    "#home-currency-shop-modal",
    "#home-stash-modal",
    "#home-sheet-panel",
    "#home-modal-root",
    "#home-delete-modal",
    "#home-settings-modal",
    "#home-skill-editor-modal",
  ] as const;

  const isHomeOverlayShown = (el: HTMLElement): boolean => {
    let cur: HTMLElement | null = el;
    while (cur) {
      if (cur.hidden) return false;
      if (cur.hasAttribute("hidden")) return false;
      cur = cur.parentElement;
    }
    return true;
  };

  const isOverlayBlockingHomeInput = (): boolean => {
    if (document.body.classList.contains("home-skill-editor-open")) return true;
    if (
      document.body.classList.contains("has-tower-modal") &&
      !isDungeonFloorEditor
    ) {
      return true;
    }
    for (const sel of OVERLAY_BLOCK_SELECTORS) {
      const el = ui.querySelector<HTMLElement>(sel);
      if (!el || !isHomeOverlayShown(el)) continue;
      return true;
    }
    return false;
  };

  const syncHomeGameInputLock = () => {
    const block = isOverlayBlockingHomeInput();
    getHomeScene()?.setWorldInputEnabled(!block);
    if (isDungeonFloorEditor) {
      ui.classList.toggle("home-game-input-locked-local", block);
      return;
    }
    const floorModalOpen = Boolean(
      document.body.classList.contains("has-tower-modal") &&
        document.querySelector(".dungeon-floor-modal"),
    );
    document.body.classList.toggle(
      "home-game-input-locked",
      block || floorModalOpen,
    );
  };

  const gameInputLockObserver = new MutationObserver(() => {
    syncHomeGameInputLock();
  });
  gameInputLockObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ["class"],
  });
  gameInputLockObserver.observe(ui, {
    attributes: true,
    attributeFilter: ["hidden", "aria-hidden"],
    subtree: true,
  });

  const tryPlacePart = (partId: string, col: number, row: number) => {
    const part = homeData.parts.find((p) => p.id === partId);
    if (!part) return false;
    const kind = normalizePartKind(part.kind);
    const hint = ui.querySelector<HTMLElement>("#home-mode-hint");

    if (kind === "backdrop") {
      homeData.backdropPartId = partId;
      homeData.backdropUrl = null;
      if (hint) hint.textContent = "ตั้งเป็นฉากหลังทั้งห้องแล้ว";
      persist();
      return true;
    }

    if (kind === "shell") {
      homeData.shellPartId = partId;
      if (hint) hint.textContent = "ตั้งเป็นฉากรอบกรอบนอกกริดแล้ว";
      persist();
      return true;
    }

    const asRoomFloor =
      kind === "floor" &&
      Boolean(ui.querySelector<HTMLInputElement>("#place-as-room-floor")?.checked);
    if (asRoomFloor) {
      homeData.floorPartId = partId;
      if (hint) hint.textContent = "ตั้งเป็นพื้นทั้งห้องแล้ว";
      persist();
      return true;
    }

    if (!canPlaceAt(homeData, partId, col, row)) {
      if (hint) hint.textContent = "วางไม่ได้ที่นี่ · ชนชิ้นอื่น เกินกริด หรือเต็มโควต้า";
      return false;
    }
    homeData.placements.push({
      id: newHomeId(),
      partId,
      gridX: col,
      gridY: row,
    });
    persist();
    return true;
  };

  const updatePlaceGhostAtClient = (clientX: number, clientY: number, partId: string) => {
    const scene = getHomeScene();
    if (!scene || !editMode) return;
    const part = homeData.parts.find((p) => p.id === partId);
    if (!part) {
      scene.clearPlaceGhost();
      return;
    }
    const cell = scene.pickGridFromClient(clientX, clientY);
    if (!cell) {
      scene.clearPlaceGhost();
      return;
    }
    if (normalizePartKind(part.kind) === "backdrop") {
      scene.setPlaceGhost({
        partId,
        cellsW: homeData.gridCols,
        cellsH: homeData.gridRows,
        col: 0,
        row: 0,
        valid: true,
      });
      return;
    }
    if (normalizePartKind(part.kind) === "shell") {
      scene.clearPlaceGhost();
      return;
    }
    const fp = partFootprint(part);
    let col = Math.max(0, Math.min(cell.col, homeData.gridCols - fp.w));
    let row = Math.max(0, Math.min(cell.row, homeData.gridRows - fp.h));
    const snapped = resolvePlaceCell(homeData, partId, col, row);
    col = snapped.col;
    row = snapped.row;
    scene.setPlaceGhost({
      partId,
      cellsW: part.cellsW,
      cellsH: part.cellsH,
      col,
      row,
      valid: canPlaceAt(homeData, partId, col, row),
    });
  };

  const placeAtClient = (clientX: number, clientY: number, partId: string) => {
    const scene = getHomeScene();
    if (!scene) return;
    const part = homeData.parts.find((p) => p.id === partId);
    if (!part) return;
    const placeKind = normalizePartKind(part.kind);
    if (placeKind === "backdrop" || placeKind === "shell") {
      tryPlacePart(partId, 0, 0);
      scene.clearPlaceGhost();
      return;
    }
    const cell = scene.pickGridFromClient(clientX, clientY);
    if (!cell) return;
    const fp = partFootprint(part);
    let col = Math.max(0, Math.min(cell.col, homeData.gridCols - fp.w));
    let row = Math.max(0, Math.min(cell.row, homeData.gridRows - fp.h));
    const snapped = resolvePlaceCell(homeData, partId, col, row);
    col = snapped.col;
    row = snapped.row;
    if (tryPlacePart(partId, col, row) && snapped.snapped) {
      const hint = ui.querySelector<HTMLElement>("#home-mode-hint");
      if (hint) hint.textContent = "snap เข้าจุดวางหน้าของทึบแล้ว";
    }
    scene.clearPlaceGhost();
  };

  container.addEventListener("dragover", (event) => {
    if (!editMode) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    const partId = draggingPartId;
    if (partId) updatePlaceGhostAtClient(event.clientX, event.clientY, partId);
  });

  container.addEventListener("dragleave", (event) => {
    if (event.relatedTarget instanceof Node && container.contains(event.relatedTarget)) {
      return;
    }
    if (!draggingPartId) getHomeScene()?.clearPlaceGhost();
  });

  container.addEventListener("drop", (event) => {
    if (!editMode) return;
    event.preventDefault();
    const partId =
      draggingPartId ||
      event.dataTransfer?.getData(HOME_PART_DRAG_MIME) ||
      event.dataTransfer?.getData("text/plain");
    draggingPartId = null;
    if (!partId) return;
    placeAtClient(event.clientX, event.clientY, partId);
  });

  container.addEventListener("pointermove", (event) => {
    if (!editMode || draggingPartId) return;
    if (!armedPartId) return;
    if ((event.target as HTMLElement).tagName !== "CANVAS") return;
    updatePlaceGhostAtClient(event.clientX, event.clientY, armedPartId);
  });

  container.addEventListener("pointerleave", () => {
    if (!draggingPartId) getHomeScene()?.clearPlaceGhost();
  });

  container.addEventListener("click", (event) => {
    if (!editMode || !armedPartId) return;
    if ((event.target as HTMLElement).tagName !== "CANVAS") return;
    event.stopPropagation();
    placeAtClient(event.clientX, event.clientY, armedPartId);
  });

  const onMovePlacement = (event: Event) => {
    const detail = (event as CustomEvent<HomeMovePlacementPayload>).detail;
    const placement = homeData.placements.find((p) => p.id === detail.placementId);
    if (!placement) return;
    if (
      !canPlaceAt(homeData, placement.partId, detail.gridX, detail.gridY, placement.id)
    ) {
      persist();
      return;
    }
    placement.gridX = detail.gridX;
    placement.gridY = detail.gridY;
    persist();
  };

  window.addEventListener(HomeEvents.MovePlacement, onMovePlacement);

  const onAdjustPartDepthBias = (event: Event) => {
    const detail = (event as CustomEvent<HomeAdjustPartDepthBiasPayload>).detail;
    const placement = homeData.placements.find((p) => p.id === detail.placementId);
    if (!placement) return;
    const part = homeData.parts.find((p) => p.id === placement.partId);
    if (!part) return;
    const current = effectivePlacementDepthBias(part, placement);
    const next = clampPlacementDepthBiasForGrid(
      normalizePartKind(part.kind),
      placement.gridY,
      part.cellsH,
      current + detail.delta,
      HOME_PLACEMENT_DEPTH_BIAS_MIN,
      HOME_PLACEMENT_DEPTH_BIAS_MAX,
    );
    if (next === current) return;
    placement.depthBias = next;
    persist();
  };

  window.addEventListener(HomeEvents.AdjustPartDepthBias, onAdjustPartDepthBias);

  const onAdjustPlacementTransform = (event: Event) => {
    const detail = (event as CustomEvent<HomeAdjustPlacementTransformPayload>).detail;
    const placement = homeData.placements.find((p) => p.id === detail.placementId);
    if (!placement) return;
    if (detail.action === "flipX") {
      placement.flipX = !placement.flipX;
      if (!placement.flipX) delete placement.flipX;
    } else if (detail.action === "flipY") {
      placement.flipY = !placement.flipY;
      if (!placement.flipY) delete placement.flipY;
    } else {
      const next = normalizePlacementRotation((placement.rotation ?? 0) + 90);
      if (next === 0) delete placement.rotation;
      else placement.rotation = next;
    }
    persist();
  };

  window.addEventListener(HomeEvents.AdjustPlacementTransform, onAdjustPlacementTransform);

  const applyPlaceScaleFromInputs = () => {
    const scaleInput = ui.querySelector<HTMLInputElement>("#place-scale");
    if (!scaleInput) return;
    const next = placeScaleFromPercent(Number(scaleInput.value));
    applyPlaceScaleValue(next);
  };

  const applyPlaceScaleValue = (next: number) => {
    const scaleInput = ui.querySelector<HTMLInputElement>("#place-scale");
    const clamped = clampPlaceScale(next);
    const placementId = syncActivePlacementFromScene();
    if (placementId) {
      const placement = homeData.placements.find((p) => p.id === placementId);
      const part = placement
        ? homeData.parts.find((p) => p.id === placement.partId)
        : undefined;
      if (!placement || !part) return;
      const current = effectivePlacementPlaceScale(part, placement);
      if (current === clamped) return;
      const partDefault = clampPlaceScale(part.placeScale ?? 1);
      if (clamped === partDefault) delete placement.placeScale;
      else placement.placeScale = clamped;
      if (scaleInput) scaleInput.value = String(placeScalePercent(clamped));
      persist();
      return;
    }
    if (!selectedPartId) return;
    const part = homeData.parts.find((p) => p.id === selectedPartId);
    if (!part) return;
    if ((part.placeScale ?? 1) === clamped) return;
    part.placeScale = clamped;
    if (scaleInput) scaleInput.value = String(placeScalePercent(clamped));
    persistCatalog();
  };

  const applyPlaceKindFromSelect = () => {
    if (!selectedPartId) return;
    const idx = homeData.parts.findIndex((p) => p.id === selectedPartId);
    if (idx < 0) return;
    const prev = homeData.parts[idx]!;
    const kind = placeKindPicker?.getValue() ?? normalizePartKind(prev.kind);
    if (prev.kind === kind) return;
    const zones: HomeZone[] = defaultZonesForKind(kind);
    homeData.parts[idx] = createPartDefinition({
      ...prev,
      kind,
      zones,
      sockets: kind === "solid" ? prev.sockets : [],
      imageDataUrl: prev.imageDataUrl,
    });
    getHomeScene()?.setShowSockets(kind === "prop" && armedPartId === selectedPartId);
    persistCatalog();
  };

  const copySelectedPlacement = (): boolean => {
    const placementId = syncActivePlacementFromScene();
    const failState = placementCopyState(placementId);
    if (!placementId || !failState.canCopy) {
      setEditFeedback(failState.title);
      return false;
    }
    const src = homeData.placements.find((p) => p.id === placementId);
    if (!src) {
      setEditFeedback("เลือกชิ้นที่วางในฉากก่อน");
      return false;
    }
    const part = homeData.parts.find((p) => p.id === src.partId);
    if (!part) {
      setEditFeedback("เลือกชิ้นที่วางในฉากก่อน");
      return false;
    }

    const fp = partFootprint(part);
    let col = src.gridX + 1;
    let row = src.gridY;
    if (
      col + fp.w > homeData.gridCols ||
      !canPlaceAt(homeData, src.partId, col, row)
    ) {
      const cell = findFirstPlaceCell(homeData, src.partId);
      if (!cell) {
        const kind = normalizePartKind(part.kind);
        setEditFeedback(
          kindQuotaRemaining(homeData, kind) <= 0
            ? `โควต้า${slotLabelTh(kind)}เต็ม · คัดลอกไม่ได้`
            : "คัดลอกไม่ได้ · ไม่มีช่องว่างบนกริด",
        );
        return false;
      }
      col = cell.col;
      row = cell.row;
    }

    const newId = newHomeId();
    homeData.placements.push({
      id: newId,
      partId: src.partId,
      gridX: col,
      gridY: row,
      depthBias: src.depthBias,
      placeScale: src.placeScale,
      flipX: src.flipX,
      flipY: src.flipY,
      rotation: src.rotation,
    });
    selectedPlacementId = newId;
    selectedPartId = src.partId;
    armedPartId = null;
    persist();
    getHomeScene()?.selectPlacement(newId);
    syncPlaceSizeControls();
    syncDeleteDockState();
    setEditFeedback(`คัดลอก ${part.name} แล้ว`);
    return true;
  };

  const bumpPlaceScale = (deltaSteps: number) => {
    const scaleInput = ui.querySelector<HTMLInputElement>("#place-scale");
    if (!scaleInput) return;
    const placementId = syncActivePlacementFromScene();
    let current = 1;
    if (placementId) {
      const placement = homeData.placements.find((p) => p.id === placementId);
      const part = placement
        ? homeData.parts.find((p) => p.id === placement.partId)
        : undefined;
      if (!placement || !part) return;
      current = effectivePlacementPlaceScale(part, placement);
    } else if (selectedPartId) {
      const part = homeData.parts.find((p) => p.id === selectedPartId);
      if (!part) return;
      current = part.placeScale ?? 1;
    } else {
      return;
    }
    applyPlaceScaleValue(current + deltaSteps * HOME_PLACE_SCALE_STEP);
  };

  const stopClickBubble = (event: Event) => {
    event.stopPropagation();
  };

  ui.querySelector("#place-scale")?.addEventListener("change", applyPlaceScaleFromInputs);
  ui.querySelector("#place-scale-minus")?.addEventListener("click", (event) => {
    stopClickBubble(event);
    bumpPlaceScale(-1);
  });
  ui.querySelector("#place-scale-plus")?.addEventListener("click", (event) => {
    stopClickBubble(event);
    bumpPlaceScale(1);
  });
  ui.querySelector("#home-nudge-up")?.addEventListener("click", (event) => {
    stopClickBubble(event);
    nudgeSelectedPlacement(0, -1);
  });
  ui.querySelector("#home-nudge-down")?.addEventListener("click", (event) => {
    stopClickBubble(event);
    nudgeSelectedPlacement(0, 1);
  });
  ui.querySelector("#home-nudge-left")?.addEventListener("click", (event) => {
    stopClickBubble(event);
    nudgeSelectedPlacement(-1, 0);
  });
  ui.querySelector("#home-nudge-right")?.addEventListener("click", (event) => {
    stopClickBubble(event);
    nudgeSelectedPlacement(1, 0);
  });
  ui.querySelector("#home-placement-copy")?.addEventListener("click", (event) => {
    stopClickBubble(event);
    copySelectedPlacement();
  });
  ui.querySelector("#home-edit-unfocus")?.addEventListener("click", () => {
    clearEditFocus();
  });
  try {
    layerPanelCollapsed = sessionStorage.getItem("home-edit-layer-collapsed") === "1";
  } catch {
    layerPanelCollapsed = false;
  }
  syncLayerPanelCollapsedUi();
  ui.querySelector("#home-edit-layer-collapse")?.addEventListener("click", (event) => {
    event.stopPropagation();
    toggleLayerPanelCollapsed();
  });
  ui.querySelector(".home-edit-layer-panel-head")?.addEventListener("click", (event) => {
    if (!layerPanelCollapsed) return;
    if ((event.target as HTMLElement).closest("#home-edit-layer-collapse")) return;
    toggleLayerPanelCollapsed();
  });

  const placeKindHost = ui.querySelector<HTMLElement>("#place-part-kind-host");
  if (placeKindHost) {
    placeKindPicker = mountKindPicker("solid", {
      id: "place-part-kind",
      showHint: false,
      compact: true,
      variant: "select",
    });
    placeKindHost.append(placeKindPicker.root);
    placeKindPicker.root.addEventListener("home-kind-change", () => applyPlaceKindFromSelect());
  }

  const partKindHost = ui.querySelector<HTMLElement>("#part-kind-host");
  if (partKindHost) {
    partKindPicker = mountKindPicker("solid", {
      id: "part-kind",
      variant: "select",
      showHint: true,
    });
    partKindHost.append(partKindPicker.root);
  }

  ui.querySelectorAll<HTMLButtonElement>("[data-shop-tab]").forEach((btn) => {
    btn.addEventListener("click", () => {
      shopTab = (btn.dataset.shopTab as HomeShopTab) || "all";
      renderPalette();
    });
  });

  const syncUtcClock = () => {
    const el = ui.querySelector<HTMLTimeElement>("#home-utc-clock");
    if (!el) return;
    const now = new Date();
    el.dateTime = now.toISOString();
    el.textContent = formatUtcClockCompact(now);
    el.title = `เวลา ${formatUtcClockLabel(now)}`;
  };

  const syncThemeFab = () => {
    const theme = themeById(homeData.themeId);
    const fab = ui.querySelector<HTMLButtonElement>("#home-theme-toggle");
    if (!fab) return;
    const icon = fab.querySelector(".home-theme-toggle-icon");
    const label = fab.querySelector(".home-theme-toggle-label");
    if (icon) icon.innerHTML = homeIcon(theme.id === "night" ? "moon" : "sun");
    if (label) label.textContent = theme.nameTh;
    fab.setAttribute(
      "aria-label",
      `โทนแสง ${theme.nameTh} · กดเพื่อสลับ`,
    );
    fab.title = themeManual
      ? `${theme.nameTh} (กำหนดเอง) · กดสลับ`
      : `${theme.nameTh} (ตามเวลา server) · กดสลับ`;
    fab.dataset.theme = theme.id;
  };

  ui.querySelector("#home-theme-toggle")?.addEventListener("click", () => {
    const next = themeById(homeData.themeId).id === "day" ? "night" : "day";
    themeManual = true;
    applyThemeToRoom(homeData, next);
    syncThemeFab();
    persist();
    const hint = ui.querySelector<HTMLElement>("#home-mode-hint");
    if (hint) hint.textContent = `โทนแสง: ${themeById(next).nameTh}`;
  });

  const syncGridInputs = () => {
    const colsInput = ui.querySelector<HTMLInputElement>("#home-grid-cols");
    const rowsInput = ui.querySelector<HTMLInputElement>("#home-grid-rows");
    if (colsInput) colsInput.value = String(homeData.gridCols);
    if (rowsInput) rowsInput.value = String(homeData.gridRows);
  };

  const applyGridFromInputs = () => {
    const colsInput = ui.querySelector<HTMLInputElement>("#home-grid-cols");
    const rowsInput = ui.querySelector<HTMLInputElement>("#home-grid-rows");
    const cols = Number(colsInput?.value ?? homeData.gridCols);
    const rows = Number(rowsInput?.value ?? homeData.gridRows);
    const result = applyGridSizeToRoom(homeData, cols, rows);
    syncGridInputs();
    persist();
    const hint = ui.querySelector<HTMLElement>("#home-mode-hint");
    if (hint) {
      hint.textContent =
        result.removed > 0
          ? `กริด ${result.cols}×${result.rows} · ตัดชิ้นนอกกริด ${result.removed} ชิ้น`
          : `กริด ${result.cols}×${result.rows}`;
    }
  };

  ui.querySelector("#home-grid-apply")?.addEventListener("click", applyGridFromInputs);
  ui.querySelector("#home-grid-cols")?.addEventListener("change", applyGridFromInputs);
  ui.querySelector("#home-grid-rows")?.addEventListener("change", applyGridFromInputs);
  ui.querySelector("#home-grid-cols-plus")?.addEventListener("click", () => {
    const colsInput = ui.querySelector<HTMLInputElement>("#home-grid-cols");
    if (colsInput) colsInput.value = String(Number(colsInput.value || homeData.gridCols) + 1);
    applyGridFromInputs();
  });
  ui.querySelector("#home-grid-rows-plus")?.addEventListener("click", () => {
    const rowsInput = ui.querySelector<HTMLInputElement>("#home-grid-rows");
    if (rowsInput) rowsInput.value = String(Number(rowsInput.value || homeData.gridRows) + 1);
    applyGridFromInputs();
  });

  ui.querySelector("#home-enter-edit")?.addEventListener("click", () => {
    if (!editMode) {
      setEditMode(true);
      openShopModal();
    } else {
      openShopModal();
    }
  });

  const railMoreToggle = ui.querySelector<HTMLButtonElement>("#home-rail-more-toggle");
  const leftRail = ui.querySelector<HTMLElement>(".home-rail--left");
  const syncRailExpanded = (expanded: boolean) => {
    const frame = ui.querySelector<HTMLElement>("#home-rail-frame");
    if (frame) {
      applyHomeRailGridLayout(frame, getHomeRailMenuLayout(), railMenuFeatures, expanded);
    }
    leftRail?.classList.toggle("home-rail--expanded", expanded);
  };
  railMoreToggle?.addEventListener("click", () => {
    const expanded = railMoreToggle.getAttribute("aria-expanded") === "true";
    const next = !expanded;
    railMoreToggle.setAttribute("aria-expanded", String(next));
    railMoreToggle.setAttribute(
      "aria-label",
      next ? "ย่อเมนูเพิ่มเติม" : "ขยายเมนูเพิ่มเติม",
    );
    const label = railMoreToggle.querySelector(".home-rail-label");
    if (label) label.textContent = next ? "ย่อ" : "เมนูเพิ่มเติม";
    syncRailExpanded(next);
  });
  syncRailExpanded(false);

  ui.querySelector("#home-rail-catalog")?.addEventListener("click", () => {
    openShopModal();
  });
  ui.querySelector("#home-open-parts-catalog")?.addEventListener("click", () => {
    openShopModal();
  });

  ui.querySelector("#home-shop-bulk-toggle")?.addEventListener("click", () => {
    setShopBulkMode(!shopBulkMode);
  });

  ui.querySelector("#home-shop-delete-selected")?.addEventListener("click", () => {
    openDeleteConfirmModal([...catalogBulkSelected]);
  });

  ui.querySelector("#home-shop-select-all")?.addEventListener("click", () => {
    const visible = visibleCatalogPartIds();
    const allVisibleSelected =
      visible.length > 0 && visible.every((id) => catalogBulkSelected.has(id));
    if (allVisibleSelected) {
      for (const id of visible) catalogBulkSelected.delete(id);
    } else {
      for (const id of visible) catalogBulkSelected.add(id);
    }
    syncBulkDeleteControls();
    renderPalette();
  });

  ui.querySelector("#home-delete-confirm")?.addEventListener("click", () => {
    executeCatalogDeletes(pendingCatalogDeleteIds);
  });

  ui.querySelector<HTMLButtonElement>("[data-open-home-settings]")?.addEventListener("click", () => {
    openSettingsModal();
  });

  ui.querySelectorAll<HTMLElement>("[data-close-home-settings]").forEach((el) => {
    el.addEventListener("click", () => closeSettingsModal());
  });

  ui.querySelector("#home-settings-sign-out")?.addEventListener("click", () => {
    void signOutUser();
  });

  ui.querySelectorAll<HTMLElement>("[data-close-delete]").forEach((el) => {
    el.addEventListener("click", () => closeDeleteConfirmModal());
  });

  ui.querySelectorAll<HTMLElement>("[data-close-shop]").forEach((el) => {
    el.addEventListener("click", () => {
      closeShopModal();
    });
  });

  ui.querySelector("#home-rail-confirm")?.addEventListener("click", () => {
    if (editMode) commitEditSession();
  });

  ui.querySelector("#home-edit-discard")?.addEventListener("click", () => {
    discardEditSession();
  });

  ui.querySelectorAll<HTMLButtonElement>("[data-open-sheet-panel]").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (!editMode) setEditMode(true);
      openSheetPanel();
    });
  });

  ui.querySelectorAll<HTMLElement>("[data-close-sheet-panel]").forEach((el) => {
    el.addEventListener("click", () => closeSheetPanel());
  });

  ui.querySelectorAll<HTMLButtonElement>("[data-open-modal]").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (!editMode) setEditMode(true);
      const id = btn.dataset.openModal as HomeModalId | undefined;
      if (id) showModalPanel(id);
    });
  });

  ui.querySelectorAll<HTMLButtonElement>("[data-close-modal]").forEach((btn) => {
    btn.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      closeModal();
    });
  });

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;
    const settingsModal = settingsModalEl();
    if (settingsModal && !settingsModal.hidden) {
      event.preventDefault();
      closeSettingsModal();
      return;
    }
    const skillsModal = skillsModalEl();
    if (skillsModal && !skillsModal.hidden) {
      event.preventDefault();
      closeSkillsModal();
      return;
    }
    const characterModal = characterModalEl();
    if (characterModal && !characterModal.hidden) {
      event.preventDefault();
      closeCharacterSheetModal();
      return;
    }
    const sheetPanel = ui.querySelector<HTMLElement>("#home-sheet-panel");
    if (sheetPanel && !sheetPanel.hasAttribute("hidden")) {
      event.preventDefault();
      closeSheetPanel();
      return;
    }
    const shopDetail = shopDetailModalEl();
    if (shopDetail && !shopDetail.hasAttribute("hidden")) {
      event.preventDefault();
      closeShopDetailModal();
      return;
    }
    const stashDetail = stashDetailModalEl();
    if (stashDetail && !stashDetail.hasAttribute("hidden")) {
      event.preventDefault();
      closeStashDetailModal();
      refreshStashUi();
      return;
    }
    const stash = stashModalEl();
    if (stash && !stash.hasAttribute("hidden")) {
      event.preventDefault();
      closeStashModal();
      return;
    }
    const currencyShop = ui.querySelector<HTMLElement>("#home-currency-shop-modal");
    if (currencyShop && !currencyShop.hasAttribute("hidden")) {
      event.preventDefault();
      closeCurrencyShopModal();
      return;
    }
    const shopRoot = ui.querySelector<HTMLElement>("#home-shop-modal");
    if (shopRoot && !shopRoot.hasAttribute("hidden")) {
      event.preventDefault();
      closeShopModal();
      return;
    }
    const modalRoot = ui.querySelector<HTMLElement>("#home-modal-root");
    if (!modalRoot || modalRoot.hasAttribute("hidden")) return;
    event.preventDefault();
    closeModal();
  };
  ui.addEventListener("keydown", onKeydown);

  const onSelectPlacement = (event: Event) => {
    const detail = (event as CustomEvent<HomeSelectPlacementPayload>).detail;
    selectedPlacementId = detail.placementId;
    if (detail.partId) {
      selectedPartId = detail.partId;
      armedPartId = null;
    }
    gmTools?.refresh();
    renderPalette();
    syncPlaceSizeControls();
    syncDeleteDockState();
    updateModeHint();
    renderEditLayerPanel();
  };
  window.addEventListener(HomeEvents.SelectPlacement, onSelectPlacement);

  ui.querySelector("#home-rail-delete")?.addEventListener("click", () => {
    const scene = getHomeScene();
    const placementId = syncActivePlacementFromScene();

    // Prefer removing one placed instance in the room.
    if (placementId) {
      homeData.placements = homeData.placements.filter((p) => p.id !== placementId);
      selectedPlacementId = null;
      scene?.clearSelectedPlacement();
      persist();
      return;
    }

    // No room selection → remove catalog part (and every placement of that type).
    if (!selectedPartId) return;
    deleteCatalogPart(selectedPartId);
  });

  ui.querySelector("#home-open-character")?.addEventListener("click", () => {
    openCharacterSheetModal();
  });
  ui.querySelector("#home-open-tower")?.addEventListener("click", () => {
    void openTowerModal(options.user);
  });
  ui.querySelector("#home-open-dungeon")?.addEventListener("click", () => {
    void openDungeonModal(options.user);
  });
  ui.querySelector("#home-open-raid")?.addEventListener("click", () => {
    void openRaidModal(options.user);
  });
  ui.querySelector("#home-open-rank")?.addEventListener("click", () => {
    void openRankModal(options.user);
  });
  ui.querySelector("#home-open-skills")?.addEventListener("click", () => {
    openSkillsModal();
  });
  ui.querySelector("#home-open-currency-shop")?.addEventListener("click", () => {
    openCurrencyShopModal("cash");
  });
  ui.querySelector("#home-open-stash")?.addEventListener("click", () => {
    openStashModal();
  });
  ui.querySelectorAll<HTMLElement>("[data-close-stash]").forEach((el) => {
    el.addEventListener("click", () => closeStashModal());
  });
  ui.querySelectorAll<HTMLElement>("[data-currency-shop-tab]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const tab = btn.dataset.currencyShopTab as CurrencyShopTab | undefined;
      if (tab) setCurrencyShopTab(tab);
    });
  });
  ui.querySelectorAll<HTMLElement>("[data-close-currency-shop]").forEach((el) => {
    el.addEventListener("click", () => closeCurrencyShopModal());
  });
  ui.querySelectorAll<HTMLElement>("[data-close-skills-modal]").forEach((el) => {
    el.addEventListener("click", () => closeSkillsModal());
  });
  const onOpenSkillsModal = () => {
    closeCharacterSheetModal();
    openSkillsModal();
  };
  window.addEventListener(HomeEvents.OpenSkillsModal, onOpenSkillsModal);
  ui.querySelectorAll<HTMLElement>("[data-close-character-sheet]").forEach((el) => {
    el.addEventListener("click", () => closeCharacterSheetModal());
  });

  ui.querySelector("#home-slot-open-store")?.addEventListener("click", () => {
    openCurrencyShopModal("gems");
  });

  const onStashUiClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const hint = ui.querySelector<HTMLElement>("#home-mode-hint");

    if (target.closest("[data-stash-sort-cycle]")) {
      stashView = { ...stashView, sort: cycleStashSortMode(stashView.sort) };
      refreshStashUi();
      return;
    }

    const openItem = target.closest<HTMLElement>("[data-stash-open-item]");
    if (openItem?.dataset.stashOpenItem) {
      if (shouldSuppressStashOpenClick()) return;
      openStashItemDetail(openItem.dataset.stashOpenItem as StashItemId);
      return;
    }

    if (target.closest("[data-close-stash-detail]")) {
      closeStashDetailModal();
      refreshStashUi();
      return;
    }

    if (target.closest("[data-stash-open-shadow-tab]")) {
      closeStashModal();
      openCharacterSheetModal();
      characterSheetModal?.openShadowTab();
      return;
    }
    if (target.closest("[data-stash-use-shadow-ap]")) {
      const pick = equippedShadow(shadowState) ?? shadowState.shadows[0];
      if (!pick) {
        if (hint) hint.textContent = "ยังไม่มีร่างเงา — สร้างที่แท็บร่างเงาก่อน";
        return;
      }
      pendingShadowApCardShadowId = pick.id;
      closeStashModal();
      openCharacterSheetModal();
      characterSheetModal?.openShadowTab();
      return;
    }

    if (target.closest("[data-stash-discard-minus]")) {
      stashView = {
        ...stashView,
        discardQty: Math.max(1, stashView.discardQty - 1),
      };
      refreshStashUi();
      return;
    }
    if (target.closest("[data-stash-discard-plus]")) {
      stashView = { ...stashView, discardQty: stashView.discardQty + 1 };
      refreshStashUi();
      return;
    }

    const discardBtn = target.closest<HTMLButtonElement>("[data-stash-discard-confirm]");
    if (discardBtn?.dataset.stashDiscardConfirm && stashView.detailItemId) {
      const itemId = discardBtn.dataset.stashDiscardConfirm as StashDiscardItemId;
      const qtyInput = ui.querySelector<HTMLInputElement>("#home-stash-discard-qty");
      const qty = Math.max(1, Math.floor(Number(qtyInput?.value ?? stashView.discardQty)));
      const result = discardStashItems(economy, itemId, qty);
      if (!result.ok) {
        if (hint) hint.textContent = result.reason;
        refreshStashUi();
        return;
      }
      economy = result.economy;
      persistEconomy();
      syncWalletHud();
      if (
        itemId === "skill_cooldown_card" ||
        itemId === "skill_ap_card" ||
        itemId === "skill_buff_duration_card" ||
        itemId === "skill_aoe_range_card" ||
        itemId === "skill_create_card" ||
        itemId === "shadow_create_card" ||
        itemId === "shadow_ap_card"
      ) {
        dispatchSkillRuntime();
      }
      const slot = getStashSlotById(economy, itemId);
      if (hint) {
        hint.textContent = `ทิ้ง ${slot?.name ?? "ไอเทม"} ×${result.discarded}`;
      }
      stashView = { ...stashView, detailItemId: null, discardQty: 1 };
      refreshStashUi();
      return;
    }

    const shopFromStash = target.closest<HTMLElement>("[data-stash-goto-shop]");
    if (shopFromStash?.dataset.stashGotoShop) {
      const tab = shopFromStash.dataset.stashGotoShop as CurrencyShopTab;
      closeStashModal();
      openCurrencyShopModal(tab);
    }
  };

  const onStashInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (input.id !== "home-stash-discard-qty") return;
    stashView = {
      ...stashView,
      discardQty: Math.max(1, Math.floor(Number(input.value) || 1)),
    };
    refreshStashUi();
  };

  const onCurrencyShopClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const hint = ui.querySelector<HTMLElement>("#home-mode-hint");

    const vaultBuy = target.closest<HTMLButtonElement>(".dungeon-vault-buy-monster");
    if (vaultBuy?.dataset.monsterId) {
      const monsterId = vaultBuy.dataset.monsterId;
      const currency = vaultBuy.dataset.currency === "cash" ? "cash" : "gold";
      const res = buyDungeonMonsterForVault(economy, dungeonShopConfig, monsterId, currency);
      if (!res.ok) {
        if (hint) {
          hint.hidden = false;
          hint.textContent = res.reason;
        }
        return;
      }
      economy = res.economy;
      persistEconomy();
      syncWalletHud();
      if (features.mergeGmCatalogs) {
        homeData = mergeGmMonsterCatalogIntoHome(homeData);
        flushSave();
      }
      syncCashShopUi();
      if (hint) {
        hint.hidden = false;
        hint.textContent = `ซื้อมอนสเตอร์เข้าคลังแล้ว (${currency === "gold" ? "ทอง" : "Cash"})`;
      }
      return;
    }

    const openDetail = target.closest<HTMLElement>("[data-shop-open-detail]");
    if (openDetail?.dataset.shopOpenDetail) {
      openShopItemDetail(openDetail.dataset.shopOpenDetail);
      return;
    }

    if (target.closest("[data-close-shop-detail]")) {
      closeShopDetailModal();
      return;
    }

    const prevBtn = target.closest<HTMLButtonElement>("[data-shop-page-prev]");
    if (prevBtn?.dataset.shopPageTab) {
      const tab = prevBtn.dataset.shopPageTab as CurrencyShopTab;
      shopPageByTab[tab] = Math.max(0, shopPageByTab[tab] - 1);
      syncCashShopUi();
      return;
    }
    const nextBtn = target.closest<HTMLButtonElement>("[data-shop-page-next]");
    if (nextBtn?.dataset.shopPageTab) {
      const tab = nextBtn.dataset.shopPageTab as CurrencyShopTab;
      shopPageByTab[tab] += 1;
      syncCashShopUi();
      return;
    }

    if (target.closest("[data-shop-qty-minus]")) {
      shopDetailQty = Math.max(1, shopDetailQty - 1);
      refreshShopDetailIfOpen();
      return;
    }
    if (target.closest("[data-shop-qty-plus]")) {
      shopDetailQty += 1;
      refreshShopDetailIfOpen();
      return;
    }
    if (target.closest("[data-shop-qty-bulk]")) {
      const item = shopDetailItemId ? getGmShopItemById(shopDetailItemId) : null;
      if (item) {
        const affordable = maxAffordableShopQuantity(economy, item);
        shopDetailQty = Math.min(
          affordable > 0 ? affordable : shopDetailQty,
          shopDetailQty + 99,
        );
      } else {
        shopDetailQty += 99;
      }
      refreshShopDetailIfOpen();
      return;
    }

    const buyBtn = target.closest<HTMLButtonElement>("[data-shop-detail-buy]");
    if (!buyBtn?.dataset.shopDetailBuy || !shopDetailItemId) return;
    const item = getGmShopItemById(buyBtn.dataset.shopDetailBuy);
    if (!item) return;
    const kindSelect = ui.querySelector<HTMLSelectElement>("#home-shop-detail-kind");
    const qtyInput = ui.querySelector<HTMLInputElement>("#home-shop-detail-qty");
    const qty = Math.max(1, Math.floor(Number(qtyInput?.value ?? shopDetailQty)));
    shopDetailQty = qty;
    if (item.fulfillment === "dungeon_floor_slot") {
      const meta = loadDungeonMeta(user.uid);
      if (meta.maxFloors + qty > DUNGEON_MAX_FLOORS_CAP) {
        if (hint) {
          hint.hidden = false;
          hint.textContent = "ชั้นด่านเต็มแล้ว";
        }
        return;
      }
    }
    const result = purchaseGmShopItemQuantity(economy, item, qty, {
      catalogPartKind: gemKindFromSelect(kindSelect ?? null, slotFocusKind()),
    });
    if (!result.ok) {
      if (hint) {
        hint.hidden = false;
        hint.textContent = result.reason;
      }
      refreshShopDetailIfOpen();
      syncCashShopUi();
      return;
    }
    economy = result.economy;
    persistEconomy();
    syncWalletHud();
    applyShopPurchaseEffects(item, result.quantity ?? qty);
    syncCashShopUi();
    if (hint) {
      const count = result.quantity ?? qty;
      hint.textContent =
        item.fulfillment === "dungeon_floor_slot"
          ? `ได้ชั้นด่าน +${count} · ไปตั้งที่หน้าด่าน`
          : `ได้ «${item.name}» ×${count} · ใช้ ${result.paid.toLocaleString("en-US")}`;
    }
    closeShopDetailModal();
  };

  const onCurrencyShopQtyInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (input.id !== "home-shop-detail-qty" || !shopDetailItemId) return;
    const item = getGmShopItemById(shopDetailItemId);
    if (!item) return;
    const raw = input.value.trim();
    if (raw === "") return;
    const parsed = Math.floor(Number(raw));
    if (!Number.isFinite(parsed) || parsed < 1) return;
    shopDetailQty = parsed;
    patchShopDetailQuantity(ui, { item, economy, quantity: parsed });
  };

  const onCurrencyShopQtyChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (input.id !== "home-shop-detail-qty" || !shopDetailItemId) return;
    const item = getGmShopItemById(shopDetailItemId);
    if (!item) return;
    const affordable = maxAffordableShopQuantity(economy, item);
    let qty = Math.max(1, Math.floor(Number(input.value) || 1));
    if (affordable > 0) qty = Math.min(qty, affordable);
    shopDetailQty = qty;
    input.value = String(qty);
    patchShopDetailQuantity(ui, { item, economy, quantity: qty });
  };

  ui.addEventListener("click", onCurrencyShopClick);
  ui.addEventListener("click", onStashUiClick);
  ui.addEventListener("input", onStashInput);
  ui.addEventListener("input", onCurrencyShopQtyInput);
  ui.addEventListener("change", onCurrencyShopQtyChange);

  const addPartsFromDrafts = (drafts: HomePartDraft[]) => {
    if (drafts.length === 0) return;
    const { accepted, skipped } = takeDraftsWithinSlots(homeData, economy, drafts);
    if (accepted.length === 0) {
      const kind = normalizePartKind(drafts[0]?.kind);
      const hint = ui.querySelector<HTMLElement>("#home-mode-hint");
      if (hint) {
        hint.textContent = `ช่อง${slotLabelTh(kind)}เต็ม · ซื้อช่องเพิ่มก่อน`;
      }
      openShopModal();
      return;
    }
    let appliedShell = false;
    for (const draft of accepted) {
      const id = newHomeId();
      const kind = normalizePartKind(draft.kind);
      homeData.parts.unshift(
        createPartDefinition({
          id,
          name: draft.name,
          imageDataUrl: imageDataUrlForStorage(draft.imageDataUrl),
          cellsW: draft.cellsW,
          cellsH: draft.cellsH,
          kind,
          tags: draft.tags,
        }),
      );
      if (kind === "shell") {
        homeData.shellPartId = id;
        appliedShell = true;
      } else if (kind === "backdrop") {
        homeData.backdropPartId = id;
        homeData.backdropUrl = null;
      }
    }
    selectedPartId = homeData.parts[0]?.id ?? null;
    armedPartId = null;
    const firstKind = normalizePartKind(accepted[0]?.kind);
    shopTab = firstKind === "solid" ? "all" : firstKind;
    ui.querySelector<HTMLElement>("#part-form-error")?.setAttribute("hidden", "true");
    persistCatalog();
    closeModal();
    closeSheetPanel();
    openShopModal();
    const hint = ui.querySelector<HTMLElement>("#home-mode-hint");
    if (skipped > 0 && hint) {
      hint.textContent = `เพิ่ม ${accepted.length} ชิ้น · ข้าม ${skipped} เพราะช่องเต็ม`;
    } else if (appliedShell && hint) {
      hint.textContent = "ตั้งเป็นฉากรอบกรอบนอกกริดแล้ว";
    }
  };

  const prepareUploadDataUrl = async (raw: string): Promise<string> => {
    return applyAutoBackgroundRemovalFromDataUrl(raw);
  };

  const sheetCropHost = ui.querySelector<HTMLElement>("#home-sheet-crop-host");
  sheetCropHandle = sheetCropHost
    ? mountHomeSheetCrop(sheetCropHost, addPartsFromDrafts)
    : undefined;

  sheetCropHost?.addEventListener("home-sheet-step", (event) => {
    const detail = (event as CustomEvent<{ label?: string }>).detail;
    const title = ui.querySelector<HTMLElement>("#home-sheet-panel-title");
    if (title && detail?.label) title.textContent = detail.label;
  });

  sheetCropHost?.addEventListener("home-sheet-upload-preset", (event) => {
    const preset = (event as CustomEvent<{ preset?: HomeSheetUploadPreset }>).detail?.preset;
    if (preset) syncSheetPanelChrome(preset);
  });

  const shopAutoRemoveBg = ui.querySelector<HTMLInputElement>("#home-shop-auto-remove-bg");
  const partAutoRemoveBg = ui.querySelector<HTMLInputElement>("#part-auto-remove-bg");
  if (shopAutoRemoveBg) bindAutoRemoveBgCheckbox(shopAutoRemoveBg);
  if (partAutoRemoveBg) bindAutoRemoveBgCheckbox(partAutoRemoveBg);

  const onRemoveBgPrefChanged = () => {
    void (async () => {
      if (!pendingPartOriginal) return;
      try {
        pendingPartImage = await prepareUploadDataUrl(pendingPartOriginal);
        syncPartSingleSteps();
      } catch {
        /* keep previous preview */
      }
    })();
  };
  window.addEventListener("home-remove-white-bg-changed", onRemoveBgPrefChanged);
  window.addEventListener("home-remove-black-bg-changed", onRemoveBgPrefChanged);

  ui.querySelector<HTMLInputElement>("#part-image")?.addEventListener("change", () => {
    const input = ui.querySelector<HTMLInputElement>("#part-image");
    const file = input?.files?.[0];
    if (!file) return;
    void (async () => {
      try {
        const dataUrl = await readImageFileAsDataUrl(file);
        pendingPartOriginal = dataUrl;
        pendingPartImage = await prepareUploadDataUrl(dataUrl);
        const size = await loadImageNaturalSize(pendingPartImage);
        pendingPartFileName = file.name;
        partSingleMode = null;
        hideFormError();
        syncPartSingleSteps();

        const nameInput = ui.querySelector<HTMLInputElement>("#part-name");
        const wInput = ui.querySelector<HTMLInputElement>("#part-w");
        const hInput = ui.querySelector<HTMLInputElement>("#part-h");
        const suggested = suggestCellsFromImage(size.width, size.height);
        if (nameInput) nameInput.value = defaultPartNameFromFile(file.name);
        if (wInput) wInput.value = String(suggested.cellsW);
        if (hInput) hInput.value = String(suggested.cellsH);
      } catch (err) {
        pendingPartImage = null;
        pendingPartOriginal = null;
        pendingPartFileName = "";
        partSingleMode = null;
        syncPartSingleSteps();
        showFormError(err instanceof Error ? err.message : "อัปโหลดไม่สำเร็จ");
        if (input) input.value = "";
      }
    })();
  });

  ui.querySelector("#part-change-image")?.addEventListener("click", () => {
    resetPartSingleForm();
    const fileInput = ui.querySelector<HTMLInputElement>("#part-image");
    if (fileInput) fileInput.value = "";
    requestAnimationFrame(() => fileInput?.click());
  });

  ui.querySelector("#part-mode-single")?.addEventListener("click", () => {
    if (!pendingPartImage) return;
    partSingleMode = "single";
    hideFormError();
    syncPartSingleSteps();
    requestAnimationFrame(() => {
      ui.querySelector<HTMLInputElement>("#part-name")?.focus({ preventScroll: true });
    });
  });

  ui.querySelector("#part-mode-multi")?.addEventListener("click", () => {
    if (!pendingPartImage || !sheetCropHandle) return;
    const dataUrl = pendingPartOriginal ?? pendingPartImage;
    const label = pendingPartFileName || "ภาพ";
    void (async () => {
      try {
        resetPartSingleForm();
        openSheetPanel({ resetWizard: false });
        await sheetCropHandle.loadSourceImage(dataUrl, label, { startStep: "crop" });
      } catch (err) {
        showFormError(err instanceof Error ? err.message : "โหลดภาพไม่สำเร็จ");
        syncPartSingleSteps();
      }
    })();
  });

  ui.querySelector("#part-add")?.addEventListener("click", () => {
    const nameInput = ui.querySelector<HTMLInputElement>("#part-name");
    const wInput = ui.querySelector<HTMLInputElement>("#part-w");
    const hInput = ui.querySelector<HTMLInputElement>("#part-h");
    const name = nameInput?.value.trim() ?? "";
    const cellsW = clampPartCells(Number(wInput?.value ?? 1));
    const cellsH = clampPartCells(Number(hInput?.value ?? 1));
    const kind = partKindPicker?.getValue() ?? "solid";

    if (!name) {
      showFormError("ใส่ชื่อชิ้นส่วน");
      return;
    }
    if (!pendingPartImage) {
      showFormError("อัปโหลดภาพชิ้นส่วนก่อน");
      return;
    }
    if (!canAddCatalogPart(homeData, economy, kind)) {
      showFormError(
        `ช่อง${slotLabelTh(kind)}เต็ม (${catalogSlotUsed(homeData, kind)}/${catalogSlotCap(economy, kind)}) · ซื้อช่องเพิ่มก่อน`,
      );
      return;
    }

    const partId = newHomeId();
    const normalizedKind = normalizePartKind(kind);
    homeData.parts.unshift(
      createPartDefinition({
        id: partId,
        name,
        imageDataUrl: imageDataUrlForStorage(pendingPartImage),
        cellsW,
        cellsH,
        kind: normalizedKind,
      }),
    );
    selectedPartId = partId;
    if (normalizedKind === "shell") {
      homeData.shellPartId = partId;
      armedPartId = null;
    } else if (normalizedKind === "backdrop") {
      homeData.backdropPartId = partId;
      homeData.backdropUrl = null;
      armedPartId = null;
    } else {
      armedPartId = partId;
    }
    shopTab = normalizedKind === "solid" ? "all" : normalizedKind;
    resetPartSingleForm();
    persistCatalog();
    closeModal();
    closeSheetPanel();
    openShopModal();
    if (normalizedKind === "shell") {
      const hint = ui.querySelector<HTMLElement>("#home-mode-hint");
      if (hint) hint.textContent = "ตั้งเป็นฉากรอบกรอบนอกกริดแล้ว";
    }
  });

  const modeHost = ui.querySelector<HTMLElement>("#home-game-mode-host");
  if (modeHost && features.gameModeControl) {
    gameModeControl = mountGameModeControl(modeHost, user);
    gameMode = gameModeControl.getMode();
  }

  const gmHost = ui.querySelector<HTMLElement>("#home-gm-tools-host");
  if (gmHost && features.gmTools) {
    gmTools = mountGmHomeTools(gmHost, {
      userId: user.uid,
      getHomeData: () => homeData,
      getSelectedPartId: () => selectedPartId,
      getWallet: () => economy.wallet,
      grantWallet: grantWalletGm,
      onHomeDataMutated: () => persistCatalog(),
      onExpGranted: () => {
        progression = loadCharacterProgression(user.uid);
        syncProgressionHud();
      },
    });
  }
  syncGameModeUi();

  const onGameModeChanged = (event: Event) => {
    const detail = (event as CustomEvent<GameModeChangedPayload>).detail;
    if (detail.userId !== user.uid) return;
    gameMode = detail.mode;
    syncGameModeUi();
  };
  if (features.gameModeControl) {
    window.addEventListener(GameEvents.GameModeChanged, onGameModeChanged);
  }

  const skillsPanelHost = ui.querySelector<HTMLElement>("#home-skills-panel-host");
  const skillsPanelHandle =
    features.skillsModals && skillsPanelHost
    ? mountSkillsPanel(skillsPanelHost, user, options.profile, {
        getSkillBar: () => skillBar,
        getEconomy: () => economy,
        embedded: true,
        onSkillBarUpdated: (nextBar) => {
          skillBar = nextBar;
          void saveSkillBar(user.uid, skillBar);
          dispatchSkillRuntime();
          syncSkillBarHud();
        },
        onEconomyUpdated: (next) => {
          economy = next;
          persistEconomy();
          syncWalletHud();
          syncCashShopUi();
        },
      })
    : undefined;

  const characterSheetShell = ui.querySelector<HTMLElement>("#home-character-modal");
  let     characterSheetModal =
    features.characterSheet && characterSheetShell
    ? mountCharacterSheetModal(characterSheetShell, {
        user,
        userId: user.uid,
        profile: options.profile,
        getProgression: () => progression,
        getEconomy: () => economy,
        getEquippedShadow: () => equippedShadow(shadowState),
        getPendingShadowApCardShadowId: () => pendingShadowApCardShadowId,
        onPendingShadowApCardConsumed: () => {
          pendingShadowApCardShadowId = null;
        },
        onProgressionUpdate: (next) => {
          progression = next;
          syncProgressionHud();
        },
        onEquipmentChange: (_state, bonus) => {
          equipmentBonus = mergeStatBonuses(
            bonus,
            shadowEquippedStatBonus(equippedShadow(shadowState)),
            rankCosmetics.statBonus,
          );
          game?.registry.set("equipmentStatBonus", equipmentBonus);
          syncCombatHpFromStats();
        },
        onEconomySettingsApplied: (next) => {
          economy = normalizeHomeEconomy(next);
          syncWalletHud();
          syncItemHotbarHud();
        },
        onEconomyUpdated: (next) => {
          economy = normalizeHomeEconomy(next);
          persistEconomy();
          syncWalletHud();
          syncCashShopUi();
        },
      })
    : undefined;

  const applyHotbarPotion = (
    itemId: StashItemId,
    options?: { silent?: boolean; skipCooldown?: boolean },
  ): boolean => {
    const hint = ui.querySelector<HTMLElement>("#home-mode-hint");
    if (!options?.skipCooldown && !itemHotbarUseReady()) {
      if (!options?.silent && hint) {
        const sec = Math.ceil(itemHotbarUseCooldownRemainingMs() / 1000);
        hint.hidden = false;
        hint.textContent = `รอใช้ไอเทมอีก ${sec} วิ`;
      }
      syncItemHotbarHud();
      return false;
    }
    const blockReason = hotbarUseBlockedReason(itemId);
    if (blockReason) {
      if (!options?.silent && hint) {
        hint.hidden = false;
        hint.textContent = blockReason;
      }
      return false;
    }
    const healthTier = tierFromStashItemId(itemId);
    const manaTier = tierFromManaStashItemId(itemId);
    if (healthTier) {
      const snap = features.progressionHud ? combatHpSnapshot() : null;
      if (!snap) {
        if (!options?.silent && hint) {
          hint.hidden = false;
          hint.textContent = "ใช้ยาเลือดได้เมื่อมีแถบ HP";
        }
        return false;
      }
      const result = useHealthPotion(economy, healthTier, snap);
      if (!result.ok) {
        if (!options?.silent && hint) {
          hint.hidden = false;
          hint.textContent = result.reason;
        }
        syncItemHotbarHud();
        return false;
      }
      economy = result.economy;
      if (!options?.skipCooldown) {
        markItemHotbarUsed();
      }
      const newCurrent = Math.min(snap.max, snap.current + result.healed);
      combatHpOverride = newCurrent;
      commitCombatHp(newCurrent);
      syncCombatHpHud(newCurrent, snap.max, 0);
      window.dispatchEvent(
        new CustomEvent(TowerEvents.HealthPotionHeal, {
          detail: { amount: result.healed },
        }),
      );
      persistEconomy();
      syncWalletHud();
      syncItemHotbarHud();
      if (!options?.silent && hint) {
        hint.hidden = false;
        hint.textContent = `ฟื้นเลือด +${result.healed} HP`;
      }
      return result.healed > 0;
    }
    if (manaTier) {
      const snap = features.progressionHud ? combatManaSnapshot() : null;
      if (!snap) return false;
      const result = useManaPotion(economy, manaTier, snap);
      if (!result.ok) {
        if (!options?.silent && hint) {
          hint.hidden = false;
          hint.textContent = result.reason;
        }
        syncItemHotbarHud();
        return false;
      }
      economy = result.economy;
      if (!options?.skipCooldown) {
        markItemHotbarUsed();
      }
      const newCurrent = Math.min(snap.max, snap.current + result.restored);
      combatManaOverride = newCurrent;
      commitCombatMana(newCurrent);
      syncCombatManaHud(newCurrent, snap.max);
      persistEconomy();
      syncWalletHud();
      syncItemHotbarHud();
      if (!options?.silent && hint) {
        hint.hidden = false;
        hint.textContent = `ฟื้นมานา +${result.restored}`;
      }
      return result.restored > 0;
    }
    return false;
  };

  const tryAutoItemHotbar = () => {
    if (!features.healthPotion || editMode || !autoItemHotbarCombatAllowed()) return;
    if (!features.progressionHud) return;
    if (!itemHotbarAutoUseReady()) return;
    const used = tryAutoHotbarPotionOnce(
      economy.itemHotbarAutoHpPercent,
      () => combatHpSnapshot(),
      () => pickAutoHotbarPotionSlot(economy),
      economy.itemHotbarAutoManaPercent,
      () => combatManaSnapshot(),
      () => pickAutoHotbarManaPotionSlot(economy),
      (itemId) => applyHotbarPotion(itemId, { silent: true, skipCooldown: true }),
    );
    if (used) {
      markItemHotbarAutoUsed();
    }
  };

  const useHotbarItem = (itemId: StashItemId, options?: { silent?: boolean }) => {
    applyHotbarPotion(itemId, options);
  };

  let unbindPlaySkillBar: (() => void) | null = null;
  if (features.skillsBar) {
    unbindPlaySkillBar = bindPlaySkillBar(ui, {
      barSelector: "#home-skill-bar",
      getSkillBar: () => skillBar,
      getUnlocked: () => economy.skillSlotsUnlocked,
      getSitRest: () => sitRestEnabled,
      setSitRest: setSitRestEnabled,
      onUseSkill: (slotIndex, skillId) => {
        useSkillSlot(slotIndex, skillId);
        tryAutoSkillSlots();
      },
      onMasterAutoChange: (enabled) => {
        emitPlaySkillBarMasterAutoCombat(enabled);
      },
      onSkillBarUpdated: () => {
        void saveSkillBar(user.uid, skillBar);
        syncSkillBarHud();
        dispatchSkillRuntime();
        tryAutoSkillSlots();
      },
    });
  }

  let unbindItemHotbar: (() => void) | null = null;
  if (features.healthPotion) {
    unbindItemHotbar = bindItemHotbar(ui, {
      prefix: "home",
      getEconomy: () => economy,
      onAssign: (index, itemId) => {
        economy = setItemHotbarSlot(economy, index, itemId);
        persistEconomy();
        syncItemHotbarHud();
      },
      onUseItem: useHotbarItem,
      onSlotAutoChange: (index, enabled) => {
        economy = setItemHotbarSlotAuto(economy, index, enabled);
        persistEconomy();
        syncItemHotbarHud();
      },
    });
  }

  const bootHomePhaser = () => {
    game?.destroy(true);
    const activePet = equippedPet(petState);
    game = startHomeRoom(container, currentGameArt, {
      homeData,
      skillRuntime: buildSkillRuntimePayload(skillBar, economy),
      progression,
      equipmentStatBonus: equipmentBonus,
      equippedPet: activePet,
      petSkillRuntime: activePet
        ? buildPetSkillRuntimePayload(activePet.skillBar, economy)
        : undefined,
      homeRoomDesignLayout: options.shell === "tower" || options.arenaLayout === true,
      homeRoomEditorZoom: isDungeonFloorEditor ? 0.8 : undefined,
      rankBadgeBoards: rankCosmetics.badgeBoards,
      rankBadgeConfigs: rankCosmetics.badgeConfigs,
      ...structureMount?.getBootExtras(),
    });
    requestAnimationFrame(() => syncHomeGameInputLock());
    dispatchSkillRuntime();
  };

  bootLoader.setProgress(90, "โหลดฉาก…");
  window.addEventListener(HomeEvents.RoomSceneReady, onBootRoomSceneReady, { once: true });
  bootSceneReadyTimer = setTimeout(() => finishBootLoader(), 15_000);
  bootHomePhaser();
  syncProgressionHud();

  const onOpenCharacterShadowTab = () => {
    openCharacterSheetModal();
    characterSheetModal?.openShadowTab();
  };
  window.addEventListener(HomeEvents.OpenCharacterShadowTab, onOpenCharacterShadowTab);

  const onShadowChanged = (event: Event) => {
    const detail = (event as CustomEvent<import("@/home/home-events").HomeShadowChangedPayload>).detail;
    shadowState = detail.state;
    currentGameArt = mergeProfileAndShadowGameArt(options.profile, detail.equipped);
    equipmentBonus = buildEquipmentBonus();
    bootHomePhaser();
    syncCombatHpFromStats();
  };
  window.addEventListener(HomeEvents.ShadowChanged, onShadowChanged);

  const onMonsterDefeated = (event: Event) => {
    const detail = (event as CustomEvent<{ xpReward: number }>).detail;
    if (detail.xpReward > 0) applyExpGrant(detail.xpReward);
  };
  const onProgressionChanged = (event: Event) => {
    const detail = (event as CustomEvent<HomeProgressionChangedPayload>).detail;
    progression = detail.progression;
    syncProgressionHud();
    syncCombatHpFromStats();
    const levelsGained = detail.levelsGained ?? 0;
    if (levelsGained > 0 && editMode) {
      const wrap = ui.querySelector<HTMLElement>(".home-stage-wrap");
      if (wrap) showHomeLevelUpDomFx(wrap, detail.progression.level);
    }
  };

  window.addEventListener(HomeEvents.MonsterDefeated, onMonsterDefeated);
  window.addEventListener(HomeEvents.ProgressionChanged, onProgressionChanged);

  const onTowerPlayerHpChanged = (event: Event) => {
    const detail = (event as CustomEvent<TowerPlayerHpChangedPayload>).detail;
    if (!detail) return;
    combatHpOverride = detail.current;
    commitCombatHp(detail.current);
    syncCombatHpHud(detail.current, detail.max, detail.damageTaken);
    if (detail.damageTaken > 0 && sitRestEnabled) {
      setSitRestEnabled(false);
    }
    if (skillBar.masterAuto) {
      emitPlaySkillBarMasterAutoCombat(true);
    }
    tryAutoSkillSlots();
    tryAutoItemHotbar();
  };
  const onTowerPlayerManaChanged = (event: Event) => {
    const detail = (event as CustomEvent<TowerPlayerManaChangedPayload>).detail;
    if (!detail) return;
    combatManaOverride = detail.current;
    commitCombatMana(detail.current);
    syncCombatManaHud(detail.current, detail.max);
    tryAutoItemHotbar();
  };
  const onTowerBattleHpReset = () => {
    resetCombatHpOverride();
    resetCombatManaOverride();
    clearTowerFocusHud();
  };

  const onTowerFocusTargetChanged = (event: Event) => {
    const detail = (event as CustomEvent<TowerFocusTargetChangedPayload>).detail;
    if (!detail) return;
    syncTowerFocusHud(detail);
  };
  const onHomeEquipmentChanged = () => {
    syncCombatHpFromStats();
  };

  const onSitRestChanged = (event: Event) => {
    const { enabled } = (event as CustomEvent<HomeSitRestPayload>).detail;
    if (sitRestEnabled === enabled) return;
    sitRestEnabled = enabled;
    syncPlaySkillBarSitRestUi(ui, enabled);
  };

  window.addEventListener(TowerEvents.PlayerHpChanged, onTowerPlayerHpChanged);
  window.addEventListener(TowerEvents.PlayerManaChanged, onTowerPlayerManaChanged);
  window.addEventListener(TowerEvents.FocusTargetChanged, onTowerFocusTargetChanged);
  window.addEventListener(TowerEvents.BattleEnded, onTowerBattleHpReset);
  window.addEventListener(TowerEvents.FloorCleared, onTowerBattleHpReset);
  window.addEventListener(HomeEvents.EquipmentChanged, onHomeEquipmentChanged);
  window.addEventListener(HomeEvents.SitRestChanged, onSitRestChanged);

  const onExternalEconomyChanged = (event: Event) => {
    const detail = (event as CustomEvent<{ economy: HomeEconomy }>).detail;
    if (!detail?.economy) return;
    economy = normalizeHomeEconomy(detail.economy);
    syncWalletHud();
    syncItemHotbarHud();
    syncCombatHpFromStats();
  };
  window.addEventListener(HomeEvents.EconomyChanged, onExternalEconomyChanged);

  const onCombatKillStreakBestChanged = () => {
    syncCombatHitBestHud();
  };
  window.addEventListener(
    CombatKillStreakEvents.BestChanged,
    onCombatKillStreakBestChanged,
  );

  const onCombatActiveChanged = (event: Event) => {
    combatActive = (event as CustomEvent<{ active: boolean }>).detail.active;
    if (combatActive) {
      if (skillBar.masterAuto) {
        emitPlaySkillBarMasterAutoCombat(true);
      }
      tryAutoSkillSlots();
      tryAutoItemHotbar();
    }
  };
  window.addEventListener(HomeEvents.CombatActiveChanged, onCombatActiveChanged);
  window.addEventListener(TowerEvents.SkillUseAccepted, onSkillUseAccepted);
  window.addEventListener(TowerEvents.SkillUseRejected, onSkillUseRejected);

  if (features.skillsBar) {
    skillCooldownTimer = setInterval(() => {
      const anyCd = Array.from({ length: SKILL_SLOT_MAX }, (_, i) => i).some(
        (index) => skillCooldownRemainingMs(index) > 0,
      );
      if (anyCd) syncSkillCooldownProgress();
      tryAutoSkillSlots();
    }, 100);
  }
  if (features.healthPotion) {
    itemHotbarCooldownTimer = setInterval(() => {
      syncItemHotbarHud();
      tryAutoItemHotbar();
    }, 250);
  }
  if (features.progressionHud) {
    combatHpRegenTimer = setInterval(() => {
      tickCombatHpRegen();
      tickCombatManaRegen();
    }, 1000);
  }
  syncUtcClock();
  utcClockTimer = setInterval(syncUtcClock, 1000);
  syncThemeFab();
  syncStageShellWrap();
  syncWalletHud();
  syncCombatHpFromStats();
  syncCombatHitBestHud();
  syncSlotBar();
  updateFurnitureCount();
  void options.saveRoom(homeData).catch(() => {
    /* schedule sync is best-effort */
  });
  flushEconomy();

  const stageWrap = ui.querySelector<HTMLElement>(".home-stage-wrap");
  const gameShell = ui.querySelector<HTMLElement>("#game-shell");
  const resizeTarget = stageWrap ?? container;
  const syncGameViewport = () => {
    if (!game) return;
    const w = resizeTarget.clientWidth;
    const h = resizeTarget.clientHeight;
    if (w > 0 && h > 0) {
      game.scale.resize(w, h);
    }
  };
  const resizeObserver = new ResizeObserver(() => {
    syncGameViewport();
  });
  resizeObserver.observe(resizeTarget);
  if (gameShell) resizeObserver.observe(gameShell);
  const onGameShellTransitionEnd = (event: TransitionEvent) => {
    if (event.propertyName === "right") syncGameViewport();
  };
  gameShell?.addEventListener("transitionend", onGameShellTransitionEnd);
  requestGameViewportSync = () => {
    requestAnimationFrame(() => syncGameViewport());
  };
  requestAnimationFrame(() => {
    syncGameViewport();
  });

  renderPalette();

  const pageQuery = new URLSearchParams(window.location.search);
  const stripPath = options.skillsQueryStripPath ?? "/home";
  if (features.skillsModals && pageQuery.get("skills") === "1") {
    window.history.replaceState({}, "", stripPath);
    openSkillsModal();
  } else if (features.characterSheet && pageQuery.get("character") === "1") {
    window.history.replaceState({}, "", stripPath);
    openCharacterSheetModal();
    characterSheetModal?.openSpriteTab();
  } else if (features.cashShop) {
    const shopTabFromUrl = parseCurrencyShopTab(pageQuery.get("shop"));
    if (shopTabFromUrl) {
      window.history.replaceState({}, "", stripPath);
      if (pageQuery.get("shop")?.toLowerCase() === "revive") {
        const reviveItem = listGmShopItemsForPlayers("cash").find(
          (entry) => entry.fulfillment === "revive_card",
        );
        if (reviveItem) {
          shopPageByTab.cash = shopPageForItem("cash", reviveItem.id);
        }
      }
      openCurrencyShopModal(shopTabFromUrl);
      if (pageQuery.get("shop")?.toLowerCase() === "revive") {
        const reviveItem = listGmShopItemsForPlayers("cash").find(
          (entry) => entry.fulfillment === "revive_card",
        );
        if (reviveItem) {
          requestAnimationFrame(() => openShopItemDetail(reviveItem.id));
        }
      }
    }
  }

  const destroy = () => {
    if (bootSceneReadyTimer) {
      clearTimeout(bootSceneReadyTimer);
      bootSceneReadyTimer = null;
    }
    window.removeEventListener(HomeEvents.RoomSceneReady, onBootRoomSceneReady);
    bootLoaderFinished = true;
    bootLoader.destroy();
    gameInputLockObserver.disconnect();
    document.body.classList.remove("home-game-input-locked");
    ui.classList.remove("home-game-input-locked-local");
    gameShell?.removeEventListener("transitionend", onGameShellTransitionEnd);
    resizeObserver.disconnect();
    if (saveTimer) clearTimeout(saveTimer);
    if (economyTimer) clearTimeout(economyTimer);
    if (skillCooldownTimer) clearInterval(skillCooldownTimer);
    if (utcClockTimer) clearInterval(utcClockTimer);
    if (editFeedbackTimer) clearTimeout(editFeedbackTimer);
    if (itemHotbarCooldownTimer) clearInterval(itemHotbarCooldownTimer);
    if (combatHpRegenTimer) clearInterval(combatHpRegenTimer);
    if (combatHpDeltaTimer) clearTimeout(combatHpDeltaTimer);
    if (focusTargetDeltaTimer) clearTimeout(focusTargetDeltaTimer);
    unbindPlaySkillBar?.();
    unbindItemHotbar?.();
    if (editSessionSnapshot) {
      applyHomeLayoutEditSnapshot(homeData, editSessionSnapshot);
      editSessionSnapshot = null;
    }
    flushSave();
    flushEconomy();
    closeModal();
    closeCharacterSheetModal();
    closeSkillsModal();
    closeSheetPanel();
    sheetCropHandle?.destroy();
    ui.removeEventListener("keydown", onKeydown);
    ui.removeEventListener("click", onCurrencyShopClick);
    ui.removeEventListener("click", onStashUiClick);
    ui.removeEventListener("input", onStashInput);
    ui.removeEventListener("input", onCurrencyShopQtyInput);
    ui.removeEventListener("change", onCurrencyShopQtyChange);
    closeStashModal();
    window.removeEventListener(HomeEvents.MovePlacement, onMovePlacement);
    window.removeEventListener(HomeEvents.AdjustPartDepthBias, onAdjustPartDepthBias);
    window.removeEventListener(HomeEvents.AdjustPlacementTransform, onAdjustPlacementTransform);
    window.removeEventListener(HomeEvents.SelectPlacement, onSelectPlacement);
    window.removeEventListener("home-remove-white-bg-changed", onRemoveBgPrefChanged);
    window.removeEventListener("home-remove-black-bg-changed", onRemoveBgPrefChanged);
    window.removeEventListener(HomeEvents.CombatActiveChanged, onCombatActiveChanged);
    window.removeEventListener(TowerEvents.SkillUseAccepted, onSkillUseAccepted);
    window.removeEventListener(TowerEvents.SkillUseRejected, onSkillUseRejected);
    window.removeEventListener(HomeEvents.MonsterDefeated, onMonsterDefeated);
    window.removeEventListener(HomeEvents.ProgressionChanged, onProgressionChanged);
    window.removeEventListener(TowerEvents.PlayerHpChanged, onTowerPlayerHpChanged);
    window.removeEventListener(TowerEvents.PlayerManaChanged, onTowerPlayerManaChanged);
    window.removeEventListener(TowerEvents.FocusTargetChanged, onTowerFocusTargetChanged);
    window.removeEventListener(TowerEvents.BattleEnded, onTowerBattleHpReset);
    window.removeEventListener(TowerEvents.FloorCleared, onTowerBattleHpReset);
    window.removeEventListener(HomeEvents.EquipmentChanged, onHomeEquipmentChanged);
    window.removeEventListener(HomeEvents.SitRestChanged, onSitRestChanged);
    window.removeEventListener(HomeEvents.EconomyChanged, onExternalEconomyChanged);
    window.removeEventListener(
      CombatKillStreakEvents.BestChanged,
      onCombatKillStreakBestChanged,
    );
    if (features.gameModeControl) {
      window.removeEventListener(GameEvents.GameModeChanged, onGameModeChanged);
    }
    window.removeEventListener(HomeEvents.OpenSkillsModal, onOpenSkillsModal);
    window.removeEventListener(HomeEvents.OpenCharacterShadowTab, onOpenCharacterShadowTab);
    window.removeEventListener(HomeEvents.ShadowChanged, onShadowChanged);
    gameModeControl?.teardown();
    gmTools?.teardown();
    structureMount?.teardown();
    skillsPanelHandle?.teardown();
    characterSheetModal?.teardown();
    game?.destroy(true);
    game = null;
    if (options.surfaceElement) {
      options.surfaceElement.innerHTML = "";
    }
  };

  if (options.fixedGrid) {
    ui.classList.add("scene-editor--fixed-grid");
  }

  if (options.autoEnterEditMode) {
    requestAnimationFrame(() => {
      setEditMode(true);
      openShopModal();
    });
  }

  return {
    destroy,
    getRoomData: () => homeData,
    flushSave,
  };
}
